// Sensitive-data protector helpers (pure functions).
// Goal: keep phone/SSN-like digit formatting and STT safeguards reusable for
// other models/agents without dragging UI/context into prompts.
// v4.28.0

import { flagVanish } from './vanishTrace';
import { getArmedExpectedType } from './expectedDataContext';
import { isDigitRewritesEnabled } from './clinicalGuards';

// ------------------------------
// Display-time helpers (UI)
// ------------------------------

export const normalizeAccents = (str) =>
  (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// ---------------------------------------------------------------------------
// LANE-AWARE number-word conversion
// ---------------------------------------------------------------------------
// BREAKING BUG (v4.27): convertEnglishNumberWords ran the SAME map on BOTH
// panes, so Spanish words like "once" (eleven in ES) were converted in English
// text (where "once" means "at one time"), and vice-versa English words like
// "two" were converted inside Spanish sentences.
//
// FIX: Accept an optional `lang` parameter ('en' | 'es', default 'en').
//   - 'en' → only English number words; Spanish words are untouched.
//   - 'es' → only Spanish number words; English words are untouched.
//
// The signature is backward-compatible: existing callers that omit `lang`
// continue to get English-only behaviour.
//
// SECONDARY BUG: the old regex replaced words in-place inside sentences,
// which caused the replaced digit to absorb surrounding whitespace when the
// word was followed by punctuation or a newline, creating run-on text.
// FIX: the replacement preserves any trailing punctuation found after the
// matched word.
// ---------------------------------------------------------------------------

const EN_NUMBER_MAP = {
  zero: '0', one: '1', two: '2', three: '3', four: '4',
  five: '5', six: '6', seven: '7', eight: '8', nine: '9',
  ten: '10', eleven: '11', twelve: '12', thirteen: '13', fourteen: '14',
  fifteen: '15', sixteen: '16', seventeen: '17', eighteen: '18', nineteen: '19',
  twenty: '20', thirty: '30', forty: '40', fifty: '50',
  sixty: '60', seventy: '70', eighty: '80', ninety: '90',
};

const ES_NUMBER_MAP = {
  cero: '0', uno: '1', dos: '2', tres: '3', cuatro: '4',
  cinco: '5', seis: '6', siete: '7', ocho: '8', nueve: '9',
  diez: '10', once: '11', doce: '12', trece: '13', catorce: '14',
  quince: '15', dieciseis: '16', diecisiete: '17', dieciocho: '18', diecinueve: '19',
  // v4.120.0: accented STT forms (regex matches raw text, so list them).
  'dieciséis': '16', 'veintidós': '22', 'veintitrés': '23', 'veintiséis': '26',
  veinte: '20', veintiuno: '21', veintidos: '22', veintitres: '23',
  veinticuatro: '24', veinticinco: '25', veintiseis: '26', veintisiete: '27',
  veintiocho: '28', veintinueve: '29',
  treinta: '30', cuarenta: '40', cincuenta: '50', sesenta: '60',
  setenta: '70', ochenta: '80', noventa: '90',
  // v4.120.0: magnitude words convert so folds below see digits.
  cien: '100', ciento: '100', mil: '1000',
};

export const convertEnglishNumberWords = (text, lang = 'en') => {
  const map = lang === 'es' ? ES_NUMBER_MAP : EN_NUMBER_MAP;
  const keys = Object.keys(map).join('|');
  // Capture optional trailing punctuation so it doesn't get swallowed.
  const re = new RegExp(`\\b(${keys})\\b([,.]?)`, 'gi');
  return (text || '').replace(re, (_, matched, punct) => {
    const digit = map[normalizeAccents(matched)];
    return digit !== undefined ? digit + punct : matched + punct;
  });
};

/**
 * Single Deepgram word → digits (v4.121.0, for confidence alignment).
 * Digits pass through; lane number-words convert; anything else → null.
 */
export const dgWordToDigits = (word, lang = 'en') => {
  const w = (word || '').toString().toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
  if (!w) return null;
  if (/^\d+$/.test(w)) return w;
  const map = lang === 'es' ? ES_NUMBER_MAP : EN_NUMBER_MAP;
  return map[normalizeAccents(w)] ?? null;
};

// ---------------------------------------------------------------------------
// DICTATION WORDS (v4.120.0) — runs BEFORE word conversion, on words.
// ---------------------------------------------------------------------------
// "8 oh 5" → "8 0 5" · "double five" → "five five" · "triple 5" → "5 5 5".
// "oh" converts ONLY next to a real digit ("oh no, my leg" is untouched);
// double/triple need a digit or number-word right after ("double room" safe).
// ---------------------------------------------------------------------------

const numWordAltFor = (lang) =>
  Object.keys(lang === 'es' ? ES_NUMBER_MAP : EN_NUMBER_MAP).join('|');

export const expandDictationWords = (text, lang = 'en') => {
  let out = text || '';
  if (lang === 'es') {
    out = out.replace(
      new RegExp(`\\bdoble\\s+(${numWordAltFor('es')}|\\d)\\b`, 'gi'), '$1 $1',
    );
    out = out.replace(
      new RegExp(`\\btriple\\s+(${numWordAltFor('es')}|\\d)\\b`, 'gi'), '$1 $1 $1',
    );
  } else {
    const dbl = numWordAltFor('en');
    out = out.replace(new RegExp(`\\bdouble\\s+(${dbl}|\\d)\\b`, 'gi'), '$1 $1');
    out = out.replace(new RegExp(`\\btriple\\s+(${dbl}|\\d)\\b`, 'gi'), '$1 $1 $1');
    // "oh" → 0 only beside a digit: "8 oh 5" ✓, "oh no" ✗.
    out = out.replace(/\boh\b/gi, (m, offset, full) => {
      const ctx = `${full.slice(Math.max(0, offset - 6), offset)} ${full.slice(offset + m.length, offset + m.length + 6)}`;
      return /\d/.test(ctx) ? '0' : m;
    });
  }
  return out;
};

// ---------------------------------------------------------------------------
// MONEY FOLD (v4.173.0) — runs FIRST, on raw words, before any other stage.
// ---------------------------------------------------------------------------
// A dental/insurance call is nothing but prices, and three stages were eating
// them with no guard at all:
//   "one fifty"        → "1 50"  → expandBareTimes  → "1:50"    (a clock!)
//   "two twenty five"  → "2 20 5"→ combineCompound → "2 25" → "2:25"
//   "150, 250, 350"    → formatPhoneAndSSNDigits  → "150-25-0350"  (an SSN!)
// A price is a NUMBER, and a number must be written down as one number. So we
// fold the spoken money form here, while it is still recognisable, and only
// when the speaker is actually talking money. No cue → byte-identical to
// v4.172.0, so appointments ("at 3 30") and dictation are untouched.
// Digits only: we never invent a currency symbol nobody said.
// ---------------------------------------------------------------------------

/** Money words / currency in either lane. Deliberately NARROW. */
const MONEY_CUE_RE =
  /(?:\$|€|£|\b(?:dollars?|bucks?|usd|pesos?|euros?|money|price|pricing|cost|costs|precio|costos?|cuesta|cobro|cobran|total|totaling|pagar|pago|pay|paid|payment|quote|quotation|estimate|estimating|budget|presupuesto|balance|remaining|owed|due|invoice|factura|billing|copay|co-?pay|copago|deductible|deducible|co-?insurance|coaseguro|out[- ]of[- ]pocket|self.?pay|sin seguro|insuran\w*|seguro)\b)/i;

/** A time cue inside this window means the digits belong to a clock.
 * v4.196.0: + by / noon / mediodía / hora(s) ("come by 3 30").
 * NOT about/around/say/make: "it's about one fifty" is a PRICE, and a clock
 * cue here vetoes the money fold → 1:50 (the v4.173.0 bug). */
const CLOCK_CUE_NEAR_RE =
  /(?:\b(?:at|by|noon|mediod[ií]a|horas?|am|pm|a\.?m\.?p?\.?m?\.?|o'?clock|sharp|hour|hours?|hrs?|minute|minutes?|appointment|appt|scheduled|schedule|slots?|time|when|starts?|starts|mañana|tarde|medianoche|noche)\b|@|a\s+las)/i;

const MONEY_WINDOW = 28;

/**
 * A dose or measurement in the same window. A repeated dose is real speech
 * ("give 500 500 mg", "one two one"), so any stage that merges or deletes
 * digit groups must brake on this exactly as it brakes on money.
 */
// "unit" is DELIBERATELY absent: it is overwhelmingly an address word
// ("1935 Madison Avenue, Unit 1") and treating it as a dosage braked a real
// dictation run. "units" on its own is rare enough to live without.
const DOSE_CUE_RE =
  /\b(?:mg|mgs|ml|mcg|ug|iu|cc|tablet|tablets|capsule|capsules|pill|pills|gota|gotas|drop|drops|spray|inhalations?|patch|dose|doses|dosage|medication|medicine|med|prescription|rx|unidades|tableta|tabletas|pastilla|medicamento|medicina|dosis|receta|vitales|vitals?|blood pressure|pressure|heart rate|pulse|temperature|weight|height|oxygen|spo2)\b/i;

/**
 * A COUNTED thing. "3 12 people", "give 2 5 tablets" — two separate numbers
 * followed by something you count, never a price. This is the veto that lets
 * the money fold run without a money word: the most common dental phrasing
 * ("the cleaning is one fifty") has no money cue at all, but a price is never
 * followed by a plural noun.
 */
const COUNT_NOUN_RE =
  /\b(?:people|persons?|patients?|adults?|children|kids|visits?|appointments?|days?|weeks?|months?|years?|times?|tablet|tablets|pills?|capsules?|doses?|hours?|minutes?|seats?|slots?|rooms?|beds?|questions?)\b/i;

/**
 * An explicit ID/phone request near the run BEATS the money veto, same rule
 * the rest of this file already uses ("request wins"). Without it, a real
 * phone number dictated during a billing call — "call me at 555 123 4567
 * about the cost" — would stop formatting, which is worse than the bug.
 *
 * v4.174.0: WORDS ONLY. v4.173.0 also matched `\d{3}[-.\s]\d{3,4}[-.\s]\d{4}`
 * as a "already-formatted ID" signal, but that pattern matches the PRICE LIST
 * ITSELF — "the estimate is 150 250 3500" satisfied it, cancelled the money
 * veto and came out 150-250-3500. A shape test can never tell a price list
 * from a phone, so only vocabulary is allowed to make this decision.
 */
const ID_REQUEST_NEAR_RE =
  /(?:\b(?:phone|cell|mobile|call me|call back|contact|tel[eé]fono|cel(?:ular)?|n[uú]mero|ssn|social security|ss#|member|member id|policy|policy number|insurance|insurance id|afiliad[oa]|expediente|folio|chart|chart number|mrn|record number|account)\b)/i;

const idRequestNear = (full, i) => {
  const a = Math.max(0, i - MONEY_WINDOW);
  const b = Math.min(full.length, i + MONEY_WINDOW);
  return ID_REQUEST_NEAR_RE.test(full.slice(a, b));
};

export const hasMoneyCue = (text) => MONEY_CUE_RE.test(text || '');

/** Money cue within MONEY_WINDOW chars of index i. */
const moneyCueNear = (full, i) => {
  const a = Math.max(0, i - MONEY_WINDOW);
  const b = Math.min(full.length, i + MONEY_WINDOW);
  return MONEY_CUE_RE.test(full.slice(a, b));
};

/** Clock cue within MONEY_WINDOW chars of index i. */
const clockCueNear = (full, i) => {
  const a = Math.max(0, i - MONEY_WINDOW);
  const b = Math.min(full.length, i + MONEY_WINDOW);
  return CLOCK_CUE_NEAR_RE.test(full.slice(a, b));
};

/**
 * Spoken price → one number. Runs on the display string, after number words
 * are digits but before compound/magnitude/time folds.
 *   "1 50"     → 150      (one fifty)          = 1 hundred + 50
 *   "2 20 5"   → 225      (two twenty five)    = 2 hundred + 2 tens + 5
 *   "1 100 50" → 1150     (one thousand one hundred fifty, post-magnitude)
 * A clock cue in the same window vetoes every fold — "at 3 30" stays 3 30.
 */
export const foldMoneyAmounts = (text) => {
  if (!text) return text;
  let out = text;

  /**
   * v4.174.0: the stage is NOT gated on a money cue, because the most common
   * dental phrasing has no money word at all — "the cleaning is one fifty" —
   * and requiring one left it as "1 50". Instead we veto on a COUNT noun.
   * That is the shape that actually broke: "3 12 people" is a headcount, and
   * folding it gave "312 people". A price is not followed by a counted thing.
   */
  const countNounAfter = (full, index) => {
    const tail = full.slice(index, index + 24);
    return COUNT_NOUN_RE.test(tail);
  };

  // The (?<!\d\s) guard is load-bearing: clerk shorthand "1 1 30" is a TRIPLE
  // (1:00, 1:30) handled later by expandClerkTimeShorthand. Without it the
  // pair rule eats the tail and "1 1 30" becomes "1 130".
  //
  // H tens units ("2 20 5" → 225) BEFORE the pair rule, else "2 20" eats it.
  // The (?!\s*\d) tail guard means a LONGER digit sequence is never folded:
  // "2 20 5 4" is four numbers, not 225 followed by a 4.
  out = out.replace(
    /(?<!\d\s)\b([1-9])\s+((?:[2-9]0|10))\s+([1-9])\b(?!\s*\d)/g,
    (m, h, t, u, off, full) =>
      clockCueNear(full, off) || countNounAfter(full, off + m.length) ? m : `${parseInt(h, 10) * 100 + parseInt(t, 10) + parseInt(u, 10)}`,
  );
  // H MM ("1 50" → 150, "3 15" → 315).
  out = out.replace(
    /(?<!\d\s)\b([1-9])\s+([0-5]\d)\b(?!\s*\d)/g,
    (m, h, mm, off, full) =>
      clockCueNear(full, off) || countNounAfter(full, off + m.length) ? m : `${parseInt(h, 10) * 100 + parseInt(mm, 10)}`,
  );
  // Thousands + hundreds ("1 100 50" → 1150). The pair rule above cannot touch
  // it: "100" is 3 digits, so \b fails after "10". This one IS money-gated:
  // it is rare in English (the map has no "thousand") and reachable mainly
  // from the Spanish lane, where a bare fold turned "1 100 23" into 1123.
  out = out.replace(
    /(?<!\d\s)\b([1-9])\s+(100)\s+(\d{1,2})\b/g,
    (m, k, _h, r, off, full) =>
      !MONEY_CUE_RE.test(full) || clockCueNear(full, off)
        ? m
        : `${parseInt(k, 10) * 1000 + 100 + parseInt(r, 10)}`,
  );
  return out;
};


// ---------------------------------------------------------------------------
// SPANISH SPOKEN MONEY (v4.196.0)
// ---------------------------------------------------------------------------
// The ES pane never converts number words (v4.27.0: "uno nunca sabe" must not
// become "1 nunca sabe"), so "ciento cincuenta pesos" never folded. This fold is
// NARROW on purpose: only a run of Spanish number words IMMEDIATELY followed by
// a currency word. Everything else stays byte-identical.
//   "ciento cincuenta pesos"        → "150 pesos"
//   "dos mil quinientos dólares"    → "2500 dólares"
//   "uno nunca sabe"                → unchanged (no currency)
// ---------------------------------------------------------------------------

const ES_HUNDREDS = {
  cien: 100, ciento: 100, doscientos: 200, doscientas: 200, trescientos: 300,
  trescientas: 300, cuatrocientos: 400, cuatrocientas: 400, quinientos: 500,
  quinientas: 500, seiscientos: 600, seiscientas: 600, setecientos: 700,
  setecientas: 700, ochocientos: 800, ochocientas: 800, novecientos: 900,
  novecientas: 900,
};

const esWordValue = (w) => {
  const k = normalizeAccents(w.toLowerCase());
  if (k === 'un' || k === 'una') return 1;
  if (ES_HUNDREDS[k] !== undefined) return ES_HUNDREDS[k];
  const v = ES_NUMBER_MAP[k];
  return v !== undefined && k !== 'mil' ? parseInt(v, 10) : null;
};

const ES_MONEY_WORD_RE = /^(?:pesos?|d[óo]lar(?:es)?|euros?|centavos?)\b/i;

export const foldSpanishSpokenMoney = (text) => {
  if (!text) return text;
  const tokens = text.split(/(\s+)/); // keep spacing
  const out = [];
  let i = 0;
  while (i < tokens.length) {
    // collect a run: number words, "y", "mil" (whitespace tokens in between)
    if (/^\s*$/.test(tokens[i])) { out.push(tokens[i]); i++; continue; }
    let j = i;
    const run = [];
    while (j < tokens.length) {
      const t = tokens[j];
      if (/^\s+$/.test(t)) { j++; continue; }
      const bare = t.toLowerCase();
      if (bare === 'mil' || bare === 'y' || esWordValue(bare) !== null) {
        run.push({ bare, at: j });
        j++;
        continue;
      }
      break;
    }
    // trim a trailing/leading "y" and require a currency word right after
    while (run.length && run[run.length - 1].bare === 'y') run.pop();
    const nextWord = tokens.slice(j).find((t) => !/^\s+$/.test(t)) || '';
    const words = run.filter((r) => r.bare !== 'y');
    if (words.length >= 1 && run[0].bare !== 'y' && ES_MONEY_WORD_RE.test(nextWord)) {
      let total = 0;
      let cur = 0;
      for (const { bare } of run) {
        if (bare === 'y') continue;
        if (bare === 'mil') { total += (cur || 1) * 1000; cur = 0; continue; }
        cur += esWordValue(bare);
      }
      const last = run[run.length - 1].at;
      out.push(String(total + cur));
      // keep the whitespace that followed the run
      i = last + 1;
      continue;
    }
    out.push(tokens[i]);
    i++;
  }
  return out.join('');
};

// ---------------------------------------------------------------------------
// MAGNITUDE FOLDING (v4.120.0) — runs AFTER word conversion, on digits.
// ---------------------------------------------------------------------------
// "one hundred twenty three" → "1 hundred 23" → "123".
// "dos mil veintiséis" → "2 1000 26" → "2026".
// Year fold is cue-gated ("born 20 26" → "born 2026"; bare "20 26" untouched).
// ---------------------------------------------------------------------------

export const foldMagnitudes = (text) => {
  let out = text || '';
  out = out.replace(/\b(\d{1,3})\s+hundred(?:\s+and)?\s+(\d{1,3})\b/gi,
    (_, h, r) => `${parseInt(h, 10) * 100 + parseInt(r, 10)}`);
  out = out.replace(/\b(\d{1,3})\s+hundred\b/gi,
    (_, h) => `${parseInt(h, 10) * 100}`);
  out = out.replace(/\b(\d{1,3})\s+thousand(?:\s+and)?\s+(\d{1,3})\b/gi,
    (_, h, r) => `${parseInt(h, 10) * 1000 + parseInt(r, 10)}`);
  out = out.replace(/\b(\d{1,3})\s+thousand\b/gi,
    (_, h) => `${parseInt(h, 10) * 1000}`);
  out = out.replace(/\b(\d{1,3})\s+mil\s+(\d{1,3})\b/gi,
    (_, h, r) => `${parseInt(h, 10) * 1000 + parseInt(r, 10)}`);
  out = out.replace(/\b(\d{1,3})\s+mil\b/gi,
    (_, h) => `${parseInt(h, 10) * 1000}`);
  out = out.replace(/\b(\d{1,3})\s+cientos?\s+(\d{1,3})\b/gi,
    (_, h, r) => `${parseInt(h, 10) * 100 + parseInt(r, 10)}`);
  out = out.replace(/\b(\d{1,3})\s+cientos?\b/gi,
    (_, h) => `${parseInt(h, 10) * 100}`);
  out = out.replace(/\b(\d)\s+1000\s+(\d{1,3})\b/g,
    (_, h, r) => `${parseInt(h, 10) * 1000 + parseInt(r, 10)}`);
  // "dos mil" → "2 1000" (mil converts first) → "2000".
  out = out.replace(/\b(\d{1,3})\s+1000\b/g,
    (_, h) => `${parseInt(h, 10) * 1000}`);
  // "100 20" (ciento veinte) adds; "1 100 23" would multiply — both safe here.
  // v4.174.0: VETO on money/dose, not "require money". Two separate spoken
  // numbers both in the hundreds ("blood pressure 100 60", "250 50") were
  // being ADDED into one wrong number. Requiring positive money evidence
  // instead would have broken "ciento veinte" → 120, because the Spanish map
  // has already turned "ciento" into 100 by the time we get here. So: a money
  // or dose word in the window means these are two separate numbers.
  out = out.replace(/\b([1-9]00)\s+(\d{1,2})\b/g, (m, h, r, off, full) => {
    const win = full.slice(Math.max(0, off - 30), off + 30);
    if (MONEY_CUE_RE.test(win) || DOSE_CUE_RE.test(win)) return m;
    return `${parseInt(h, 10) + parseInt(r, 10)}`;
  });
  out = out.replace(/\b(\d{1,3})\s+100\s+(\d{1,2})\b/g, (_, h, r) => {
    const base = parseInt(h, 10);
    return `${base % 100 === 0 ? base + parseInt(r, 10) : base * 100 + parseInt(r, 10)}`;
  });
  // cue-gated years: "born 20 26" → "born 2026".
  out = out.replace(
    /\b(year|born|nacido|a[ñn]o)\s+(\d{2})\s+(\d{2})\b/gi, '$1$2$3',
  );
  return out;
};

// ---------------------------------------------------------------------------
// COMPOUND + DECIMAL numbers (v4.115.0)
// ---------------------------------------------------------------------------
// convertEnglishNumberWords leaves "eighty two" → "80 2", "ochenta y dos" →
// "80 y 2", "two point five" → "2 point 5" — fragments stitch/phone then eat.
// Combine AFTER word conversion, BEFORE clerk expansion:
//   EN "80 2" → "82" · ES "80 y 2" → "82" · "2 point 5"/"2 punto/coma 5" → "2.5"
// ---------------------------------------------------------------------------

export const combineCompoundNumbers = (text, lang = 'en') => {
  let out = text || '';
  if (lang === 'es') {
    out = out.replace(/\b([2-9]0)\s+y\s+([1-9])\b/gi, (_, t, u) => `${parseInt(t, 10) + parseInt(u, 10)}`);
    out = out.replace(/\b(\d+)\s+(?:punto|coma)\s+(\d+)\b/gi, '$1.$2');
  } else {
    out = out.replace(/\b([2-9]0)\s+([1-9])\b/g, (_, t, u) => `${parseInt(t, 10) + parseInt(u, 10)}`);
    out = out.replace(/\b(\d+)\s+point\s+(\d+)\b/gi, '$1.$2');
  }
  return out;
};

// ---------------------------------------------------------------------------
// PHONE vs SSN formatting
// ---------------------------------------------------------------------------
// v4.27 always formatted 9-digit runs as phone numbers, turning SSNs
// (NNN-NN-NNNN) into phone-style (NNN-NNN-NNN).
// FIX: 9 digits → SSN format (NNN-NN-NNNN).
//      10 digits → standard US phone (NNN-NNN-NNNN).
//      11 digits starting with 1 → +1 NNN-NNN-NNNN.
//      Other lengths → digit groups of 3 with dashes (unchanged).
// ---------------------------------------------------------------------------

export const formatPhoneAndSSNDigits = (text, { ignoreAddressGuard = false, ignoreDateGuard = false, allowNonStandardDash = false, expectedType = null, ssnCue = true } = {}) => {
  if (!text) return text;
  // v4.117.0: armed ID request overrides the ZIP/MRN verbatim brakes below.
  const armedFull =
    expectedType === 'phone' || expectedType === 'ssn' || expectedType === 'member';
  const armedId = expectedType === 'ssn' || expectedType === 'member';
  return text.replace(/\b(?:\d[\s.,-:]*){7,15}\d\b/g, (m, offset, full) => {
    // v4.114.0: clock times are NEVER phones — "1:00, 1:30, 2:00, 2:30"
    // reads as one 12-digit run to this regex. Colons don't appear in phones.
    if (m.includes(':')) return m;
    // v4.115.0: ZIP+4 is not an SSN — "10027-1234" stays verbatim.
    // v4.117.0: unless an SSN/member ID was just asked for (request wins).
    if (!armedId && /^\d{5}-\d{4}$/.test(m.trim())) return m;
    // v4.115.0: IPs/versions are NEVER phones — "192.168.1.1" (10 digits)
    // must not become a phone number. 2+ dots with digits around = not a phone.
    if (/\d\.\d/.test(m) && (m.match(/\./g) || []).length >= 2) return m;
    const before = full.slice(Math.max(0, offset - 40), offset);
    const after = full.slice(offset + m.length, offset + m.length + 40);
    // v4.120.0: provider IDs are NEVER phones — "NPI 1234567890" stays
    // verbatim even when armed (an NPI is not a phone number, full stop).
    if (/\b(?:npi|dea|ncpdp)\b/i.test(`${before} ${after}`)) return m;
    // v4.173.0: a money cue near the run means this is a PRICE LIST, not an
    // ID. "the estimate is 150, 250, 350 for the crowns" is 9 digits and used
    // to become 150-25-0350 — an SSN out of a dental quote. A real SSN/phone
    // is never announced next to a price... unless it IS being asked for
    // ("call me at 555 123 4567 about the cost"), where the request wins.
    // v4.174.0: the armed request ("can I have your SSN?") outranks the money
    // veto — same rule v4.117 established, which the v4.173.0 check bypassed
    // by running before armedFull was consulted.
    if (
      !armedFull &&
      moneyCueNear(full, offset + m.length / 2) &&
      !idRequestNear(full, offset + m.length / 2)
    ) {
      return m;
    }
    if (!ignoreAddressGuard && looksLikeAddressFragment(before, after)) return m;
    if (!ignoreDateGuard && looksLikeDateFragment(before, after)) return m;

    const digitsOnly = m.replace(/\D/g, '');
    // v4.115.0: non-phone/SSN lengths (8, 12-16 digits) next to chart cues are
    // MRNs/record numbers — leave undashed. 9/10/11 keep SSN/phone/member
    // behavior (Phase G insurance IDs).
    // v4.117.0: armed phone/ssn/member request overrides (request wins).
    if (
      !armedFull &&
      ![9, 10, 11].includes(digitsOnly.length) &&
      /\b(?:mrn|chart|record|account|afiliado|miembro|expediente|historia|folio)\b/i.test(before)
    ) {
      return m;
    }
    if (digitsOnly.length === 9) {
      // v4.197.0: SSN shape NNN-NN-NNNN only with an SSN/ID cue (sentinel or
      // armed request). No cue = still grouped for reading (v4.15 rule), but
      // as neutral NNN NNN NNN — an MRN / ES mobile / tracking code is not an
      // SSN. Every digit stays, same order.
      if (!ssnCue && !armedId) {
        return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6)}`;
      }
      // SSN: NNN-NN-NNNN
      return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 5)}-${digitsOnly.slice(5)}`;
    }
    if (digitsOnly.length === 10) {
      // US phone: NNN-NNN-NNNN
      return `${digitsOnly.slice(0, 3)}-${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    }
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      // US phone with country code
      return `+1 ${digitsOnly.slice(1, 4)}-${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
    }
    // v4.116.0 "when in doubt show both": non-US lengths (8, 12-16 digits)
    // stay EXACTLY as dictated unless an explicit phone/ssn sentinel asked
    // for grouping — a guessed dash pattern is worse than spaced digits.
    if (!allowNonStandardDash) return m;
    return digitsOnly.replace(/(\d{3})(?=\d)/g, '$1-');
  });
};

// ---------------------------------------------------------------------------
// Stitch back-to-back single-digit tokens (phone/SSN dictation)
// ---------------------------------------------------------------------------
// Industry pattern: collapse "5 5 5 1 2 3 4" → "5551234" BEFORE phone grouping.
// Only joins tokens that are exactly one digit; skips address-like context.
// ---------------------------------------------------------------------------

const SINGLE_DIGIT_RUN_RE = /\b\d(?:[\s,./-]+\d)+\b/g;

/** Month token → 1–12 (EN + ES). */
const MONTH_TO_NUM = {
  jan: 1, january: 1, enero: 1,
  feb: 2, february: 2, febrero: 2,
  mar: 3, march: 3, marzo: 3,
  apr: 4, april: 4, abril: 4,
  may: 5, mayo: 5,
  jun: 6, june: 6, junio: 6,
  jul: 7, july: 7, julio: 7,
  aug: 8, august: 8, agosto: 8,
  sep: 9, sept: 9, september: 9, septiembre: 9, setiembre: 9,
  oct: 10, october: 10, octubre: 10,
  nov: 11, november: 11, noviembre: 11,
  dec: 12, december: 12, diciembre: 12,
};

const MONTH_ALT = Object.keys(MONTH_TO_NUM).sort((a, b) => b.length - a.length).join('|');

const ORDINAL_TO_DAY = {
  first: 1, second: 2, third: 3, fourth: 4, fifth: 5, sixth: 6, seventh: 7,
  eighth: 8, ninth: 9, tenth: 10, eleventh: 11, twelfth: 12, thirteenth: 13,
  fourteenth: 14, fifteenth: 15, sixteenth: 16, seventeenth: 17, eighteenth: 18,
  nineteenth: 19, twentieth: 20, 'twenty-first': 21, 'twenty first': 21,
  'twenty-second': 22, 'twenty second': 22, 'twenty-third': 23, 'twenty third': 23,
  thirtieth: 30, 'thirty-first': 31, 'thirty first': 31,
  primero: 1, primera: 1, segundo: 2, segunda: 2, tercero: 3, tercera: 3,
  cuarto: 4, cuarta: 4, quinto: 5, quinta: 5, sexto: 6, sexta: 6,
  septimo: 7, séptimo: 7, octavo: 8, noveno: 9, decimo: 10, décimo: 10,
};

const pad2 = (n) => String(n).padStart(2, '0');

const normalizeYear = (yRaw) => {
  const y = parseInt(String(yRaw).replace(/\D/g, ''), 10);
  if (!Number.isFinite(y)) return null;
  if (y >= 1000 && y <= 2100) return y;
  if (y >= 0 && y <= 99) return y >= 30 ? 1900 + y : 2000 + y;
  return null;
};

const monthNumFromToken = (tok) => {
  const k = (tok || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return MONTH_TO_NUM[k] || null;
};

const dayFromToken = (tok) => {
  const t = (tok || '').toLowerCase().replace(/,/g, '').trim();
  if (/^\d{1,2}(st|nd|rd|th)?$/i.test(t)) {
    const d = parseInt(t, 10);
    return d >= 1 && d <= 31 ? d : null;
  }
  const ord = ORDINAL_TO_DAY[t] || ORDINAL_TO_DAY[t.replace(/\s+/g, ' ')];
  return ord || null;
};

const isoFromParts = (year, month, day) => {
  if (!year || !month || !day) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return `${year}-${pad2(month)}-${pad2(day)}`;
};

// v4.116.0 "when in doubt show both": ambiguous all-digit dates copy BOTH
// readings — "05/12/1980" → "1980-05-12 / 1980-12-05". Display untouched.
const ambiguousCopy = (year, a, b, primary) => {
  if (!primary || a === b || a < 1 || a > 12 || b < 1 || b > 31) return primary;
  const alt = isoFromParts(year, b, a);
  return alt && alt !== primary ? `${primary} / ${alt}` : primary;
};

/**
 * Find date spans that must highlight/copy as one unit (Phase B).
 * Does not rewrite display text — only reports spans + preferred copy value.
 */
export const findDateUnits = (text) => {
  if (!text) return [];
  const units = [];
  const push = (start, end, raw, iso) => {
    if (start < 0 || end <= start) return;
    // skip overlaps
    if (units.some((u) => !(end <= u.start || start >= u.end))) return;
    units.push({
      start,
      end,
      text: text.slice(start, end),
      iso: iso || null,
      copyValue: iso || text.slice(start, end).trim(),
    });
  };

  // 1) Numeric: M/D/YYYY or D/M/YYYY (ambiguous → still one span; ISO only if unambiguous US-ish)
  const numericRe = /\b(\d{1,2})[/-](\d{1,2})(?:[/-](\d{2,4}))?\b/g;
  let m;
  while ((m = numericRe.exec(text))) {
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    const year = m[3] ? normalizeYear(m[3]) : null;
    let iso = null;
    if (year && a >= 1 && a <= 12 && b >= 1 && b <= 31) {
      iso = isoFromParts(year, a, b); // prefer MDY when first ≤12
      iso = ambiguousCopy(year, a, b, iso);
    } else if (year && b >= 1 && b <= 12 && a >= 1 && a <= 31 && a > 12) {
      iso = isoFromParts(year, b, a); // DMY when day>12
    }
    push(m.index, m.index + m[0].length, m[0], iso);
  }

  // v4.115.0: dotted ES dictation "05.12.1980" — full year required so "v1.2"
  // and "room 4.5" never match.
  const dottedRe = /\b(\d{1,2})\.(\d{1,2})\.(\d{4})\b/g;
  while ((m = dottedRe.exec(text))) {
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    const year = normalizeYear(m[3]);
    let iso = null;
    if (a >= 1 && a <= 12 && b >= 1 && b <= 31) iso = ambiguousCopy(year, a, b, isoFromParts(year, a, b));
    else if (b >= 1 && b <= 12 && a >= 1 && a <= 31) iso = isoFromParts(year, b, a);
    if (!iso) continue;
    push(m.index, m.index + m[0].length, m[0], iso);
  }

  // v4.115.0: spaced slow dictation "my DOB is 05 12 1980" — cue required.
  // Masked like other dates so stitch/phone can never tear it apart.
  const spacedRe =
    /\b(?:born|dob|birthday|appointment|appt|fecha|nacimiento|cita)\b[^.\n]{0,40}?(\d{1,2})\s+(\d{1,2})\s+(\d{4})\b/gi;
  while ((m = spacedRe.exec(text))) {
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    const year = normalizeYear(m[3]);
    let iso = null;
    if (a >= 1 && a <= 12 && b >= 1 && b <= 31) iso = ambiguousCopy(year, a, b, isoFromParts(year, a, b));
    else if (b >= 1 && b <= 12 && a >= 1 && a <= 31) iso = isoFromParts(year, b, a);
    if (!iso) continue;
    // Span starts at the digit triple (tail), not at the cue word.
    const tail = m[0].match(/(\d{1,2}\s+\d{1,2}\s+\d{4})\s*$/);
    const dateText = tail ? tail[1] : `${m[1]} ${m[2]} ${m[3]}`;
    const start = m.index + m[0].lastIndexOf(dateText);
    const end = start + dateText.length;
    push(start, end, text.slice(start, end), iso);
  }

  // 2) Month name + day + year: May 8 1990 / May 8, 1990 / May 8th 1990
  const mdY = new RegExp(
    `\\b((?:${MONTH_ALT}))\\s+(\\d{1,2}(?:st|nd|rd|th)?|[A-Za-zÁÉÍÓÚáéíóúñÑ-]+)\\s*,?\\s+(\\d{2,4})\\b`,
    'gi',
  );
  while ((m = mdY.exec(text))) {
    const month = monthNumFromToken(m[1]);
    const day = dayFromToken(m[2]);
    const year = normalizeYear(m[3]);
    push(m.index, m.index + m[0].length, m[0], isoFromParts(year, month, day));
  }

  // 3) Day + month + year: 8 May 1990 / 8 de mayo de 1990
  const dmy = new RegExp(
    `\\b(\\d{1,2}(?:st|nd|rd|th)?|[A-Za-zÁÉÍÓÚáéíóúñÑ-]+)\\s+(?:de\\s+)?((?:${MONTH_ALT}))\\s+(?:de\\s+)?(\\d{2,4})\\b`,
    'gi',
  );
  while ((m = dmy.exec(text))) {
    const day = dayFromToken(m[1]);
    const month = monthNumFromToken(m[2]);
    const year = normalizeYear(m[3]);
    if (!day || !month || !year) continue;
    push(m.index, m.index + m[0].length, m[0], isoFromParts(year, month, day));
  }

  return units.sort((a, b) => a.start - b.start);
};

/** True if digit run sits next to a month/year date context — do not stitch. */
export const looksLikeDateFragment = (textBefore, textAfter) => {
  const before = (textBefore || '').slice(-48);
  const after = (textAfter || '').slice(0, 48);
  const monthRe = new RegExp(`\\b(?:${MONTH_ALT})\\b`, 'i');
  if (monthRe.test(before) || monthRe.test(after)) return true;
  if (/\b(?:born|dob|birthday|appointment|appt|fecha|nacimiento|cita)\b/i.test(before)) return true;
  // v4.114.0: clerk slot lists — "we have 1 1 30" adjacent to availability nouns.
  if (/\b(?:available|availability|spots?|openings?|slots?|scheduled?|appointment|appt|disponibles?|disponibilidad|huecos?|cupos?|turnos?)\b/i.test(before)) return true;
  // year sitting after a lone day digit
  if (/^\s*(?:de\s+)?(?:\d{4}|'\d{2})\b/i.test(after) && /\b\d{1,2}\s*$/.test(before)) return true;
  return false;
};

const DATE_MASK = (i) => `\uE000D${i}\uE001`;

/** Mask date spans so stitch/phone format cannot tear them apart. */
export const maskDateUnits = (text) => {
  const units = findDateUnits(text);
  if (!units.length) return { text, units: [], restore: (t) => t };
  let out = '';
  let cursor = 0;
  units.forEach((u, i) => {
    out += text.slice(cursor, u.start);
    out += DATE_MASK(i);
    cursor = u.end;
  });
  out += text.slice(cursor);
  const restore = (masked) => {
    let restored = masked;
    units.forEach((u, i) => {
      restored = restored.split(DATE_MASK(i)).join(u.text);
    });
    return restored;
  };
  return { text: out, units, restore };
};

/**
 * Split for highlight/copy: date → dosage → money units, then number regex on gaps.
 * @returns {{ type: 'text'|'number'|'date'|'schedule'|'dosage'|'money'|'address'|'email', value: string, copyValue?: string }[]}
 */
// v4.115.0 unit list: pills/tablets/drops/puffs + ES gotas/pastillas/tabletas.
const DOSAGE_UNITS_ALT =
  'mg|mcg|g|ml|cc|iu|units?|mEq|milligrams?|micrograms?|grams?|milliliters?|miligramos?|microgramos?|gramos?|mililitros?|unidades?|tablets?|tabletas?|pills?|pastillas?|capsules?|c[aá]psulas?|drops?|gotas?|puffs?|sprays?|tsp|tbsp|oz|%';

export const findDosageUnits = (text) => {
  if (!text) return [];
  const units = [];
  const re = new RegExp(`\\b\\d+(?:[.,]\\d+)?\\s*(?:${DOSAGE_UNITS_ALT})\\b`, 'gi');
  let m;
  while ((m = re.exec(text))) {
    const raw = m[0];
    units.push({
      start: m.index,
      end: m.index + raw.length,
      text: raw,
      copyValue: raw.replace(/\s+/g, ' ').trim(),
    });
  }
  return units;
};

export const findMoneyUnits = (text) => {
  if (!text) return [];
  const units = [];
  // v4.115.0: thousand separators — "$1,234.56" is ONE unit, not "$1,23"+"4.56".
  const re = /(?:[$€£]\s*\d+(?:[.,]\d{3})*(?:[.,]\d{2})?|\b\d+(?:(?:[.,]\d{3})+)?(?:[.,]\d{2})?\s*(?:dollars?|pesos?|usd|ars|copay|co-pay|copago|coaseguro)\b)/gi;
  let m;
  while ((m = re.exec(text))) {
    const raw = m[0];
    const digits = raw.replace(/[^\d.,]/g, '');
    units.push({
      start: m.index,
      end: m.index + raw.length,
      text: raw,
      copyValue: digits || raw.trim(),
    });
  }
  return units;
};

const STREET_TYPE_WORD =
  '(?:street|st|avenue|ave|boulevard|blvd|drive|dr|road|rd|lane|ln|court|ct|place|pl|way|circle|cir|highway|hwy|parkway|pkwy|terrace|ter|calle|avenida|av|carrera|cra|bulevar)';

const US_STATE_NAMES =
  'Alabama|Alaska|Arizona|Arkansas|California|Colorado|Connecticut|Delaware|Florida|Georgia|Hawaii|Idaho|Illinois|Indiana|Iowa|Kansas|Kentucky|Louisiana|Maine|Maryland|Massachusetts|Michigan|Minnesota|Mississippi|Missouri|Montana|Nebraska|Nevada|New Hampshire|New Jersey|New Mexico|New York|North Carolina|North Dakota|Ohio|Oklahoma|Oregon|Pennsylvania|Rhode Island|South Carolina|South Dakota|Tennessee|Texas|Utah|Vermont|Virginia|Washington|West Virginia|Wisconsin|Wyoming';

/** Address spans for highlight/copy (street #, unit, zip) — display only. */
export const findAddressUnits = (text) => {
  if (!text) return [];
  const units = [];
  const overlaps = (start, end) =>
    units.some((u) => !(end <= u.start || start >= u.end));

  const push = (start, end, raw, copyValue) => {
    if (start < 0 || end <= start || overlaps(start, end)) return;
    units.push({
      start,
      end,
      text: raw,
      copyValue: copyValue ?? raw.trim(),
    });
  };

  const streetBeforeType = new RegExp(
    `\\b(\\d{1,6})\\s*,?\\s*((?:[A-Za-zÁÉÍÓÚÑáéíóúñ][\\w.'-]*\\s+){0,2}${STREET_TYPE_WORD})\\b`,
    'gi',
  );
  let m;
  while ((m = streetBeforeType.exec(text))) {
    // v4.118.0: whole span is ONE address unit ("123 Main Street",
    // "3247 E Avenida") — click copies the full street line.
    const raw = m[0];
    push(m.index, m.index + raw.length, raw, raw.trim());
  }

  const numberCue = /\b(?:number|no\.?|#)\s+(\d{1,6})\b/gi;
  while ((m = numberCue.exec(text))) {
    const num = m[1];
    push(m.index + m[0].indexOf(num), m.index + m[0].indexOf(num) + num.length, num, copyableDigits(num));
  }

  // v4.121.0: rooms/beds/PO boxes/extensions ("Room 402", "Bed 12",
  // "PO Box 1234", "ext 123"). Digit-gated so "double room" never matches.
  const unitRe = /\b(?:unit|apt|apartment|suite|ste|#|room|bed|po box|ext\.?|extension|x)\s*,?\s*#?\s*(\d{1,6}[A-Za-z]?)\b/gi;
  while ((m = unitRe.exec(text))) {
    const raw = m[0];
    // v4.118.0: keep letter suffixes — "apt 4B" copies "4B", not "4".
    push(m.index, m.index + raw.length, raw, m[1]);
  }

  // v4.118.0: shorthand suites ("s 1", "u 4B") — gated on address context
  // in the same bubble (state, street word, or an existing address unit)
  // so "vitamin s 1" never matches.
  const hasAddressContext =
    new RegExp(`\\b(?:${US_STATE_NAMES})\\b`, 'i').test(text) ||
    STREET_TYPE_RE.test(text) ||
    units.length > 0;
  if (hasAddressContext) {
    const shortUnitRe = /\b([sua])\s*,?\s*#?\s*(\d{1,6}[A-Za-z]?)\b/gi;
    while ((m = shortUnitRe.exec(text))) {
      const raw = m[0];
      push(m.index, m.index + raw.length, raw, raw.trim());
    }
  }

  const stateZip = new RegExp(`\\b(?:${US_STATE_NAMES})\\s*,?\\s*(\\d{5})(?:-(\\d{4}))?\\b`, 'gi');
  while ((m = stateZip.exec(text))) {
    const zip = m[1] + (m[2] ? `-${m[2]}` : '');
    push(m.index + m[0].indexOf(m[1]), m.index + m[0].indexOf(m[1]) + zip.length, zip, zip);
  }

  const zipCue = /\b(?:zip(?:\s*code)?|postal(?:\s*code)?|c[oó]digo postal)\s*(?:is\s*)?(\d{5})(?:-(\d{4}))?\b/gi;
  while ((m = zipCue.exec(text))) {
    const zip = m[1] + (m[2] ? `-${m[2]}` : '');
    push(m.index + m[0].indexOf(m[1]), m.index + m[0].indexOf(m[1]) + zip.length, zip, zip);
  }

  // v4.115.0: Spanish street-first order — "Calle 45 # 12-34",
  // "Avenida 6-23", "calle Juárez 420". Whole span is one unit so
  // stitch/phone can never tear the number off the street.
  const esStreetRe =
    /\b(calle|avenida|av\.?|carrera|cra\.?|bulevar)\s+([A-Za-zÁÉÍÓÚÑáéíóúñ.'-]*\s*)?#?\s*(\d{1,6}(?:\s*[–-]\s*\d{1,6})?)/gi;
  while ((m = esStreetRe.exec(text))) {
    const raw = m[0];
    push(m.index, m.index + raw.length, raw, copyableDigits(m[3]));
  }

  return units.sort((a, b) => a.start - b.start);
};

/** Email spans for highlight/copy. */
export const findEmailUnits = (text) => {
  if (!text) return [];
  const units = [];
  const re = /\b[A-Za-z0-9](?:[A-Za-z0-9._%+-]*[A-Za-z0-9])?@[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?\.[A-Za-z]{2,}\b/g;
  let m;
  while ((m = re.exec(text))) {
    const raw = m[0];
    units.push({
      start: m.index,
      end: m.index + raw.length,
      text: raw,
      copyValue: raw,
    });
  }
  return units;
};

/**
 * Spoken email spans (v4.115.0): "juan at gmail dot com" / "maría arroba
 * correo punto com". Display stays verbatim — only highlight + clipboard
 * reconstruct "user@domain.tld". Requires an "at ... dot" chain so
 * "meet at noon tomorrow" never matches.
 */
export const findSpokenEmailUnits = (text) => {
  if (!text) return [];
  const units = [];
  const re =
    /\b([A-Za-z0-9._%+-]+)\s+(?:at|arroba)\s+([A-Za-z0-9-]+(?:\s+(?:dot|punto)\s+[A-Za-z0-9-]+)+)/gi;
  let m;
  while ((m = re.exec(text))) {
    const addr = `${m[1]}@${m[2].replace(/\s+(?:dot|punto)\s+/gi, '.')}`;
    units.push({ start: m.index, end: m.index + m[0].length, text: m[0], copyValue: addr });
  }
  return units.sort((a, b) => a.start - b.start);
};

/**
 * v4.202.0: spoken email → written email, IN THE TEXT (both panes + MT input).
 * "Mercedes punto r 56 arroba iCloud punto com" → "mercedes.r56@icloud.com".
 * v4.115.0 only highlighted; the text stayed spoken, the copy kept just "56@…",
 * and the translator turned "punto"/"arroba" into "point"/"at".
 * Safety: the domain MUST end in a known TLD ("at noon" never matches).
 * Local part joins: word, then (separator word | single letter | digits)*.
 */
const EMAIL_DOT = '(?:punto|dot|point|period)';
const EMAIL_SEP = `(?:${EMAIL_DOT}|gui[oó]n\\s+bajo|underscore|gui[oó]n|dash|hyphen)`;
const EMAIL_TLD = '(?:com|net|org|edu|gov|info|io|co|us|mx|ar|es|cl|pe|uy|ve|ec|bo|py|do|pr|gt|hn|sv|ni|cr|pa)';
const SPOKEN_EMAIL_RE = new RegExp(
  `\\b([A-Za-z0-9]+(?:\\s+${EMAIL_SEP}\\s+[A-Za-z0-9]+|\\s+[A-Za-z]\\b|\\s+\\d+\\b|[._-][A-Za-z0-9]+)*)` +
  `\\s+(?:arroba|at\\s+sign|at)\\s+` +
  `([A-Za-z0-9-]+(?:(?:\\s+${EMAIL_DOT}\\s+|\\.)[A-Za-z0-9-]+)*(?:\\s+${EMAIL_DOT}\\s+|\\.)${EMAIL_TLD})\\b`,
  'gi'
);
// Words that can sit in front of the address but are not part of it.
const EMAIL_LEAD_STOP = new Set(['is', 'es', 'my', 'mi', 'su', 'your', 'the', 'el', 'la', 'email', 'correo', 'its', 'a']);

const spokenSepToChar = (sep) => {
  const s = sep.toLowerCase();
  if (/bajo|underscore/.test(s)) return '_';
  if (/gui|dash|hyphen/.test(s)) return '-';
  return '.';
};

export const foldSpokenEmails = (text) => {
  if (!text || !/\b(arroba|at)\b/i.test(text)) return text;
  const sepRe = new RegExp(`\\s+(${EMAIL_SEP})\\s+`, 'gi');
  return text.replace(SPOKEN_EMAIL_RE, (whole, local, domain) => {
    // Tokens split on spaces; separators become one-char tokens.
    const tokens = local.replace(sepRe, (m, sep) => ` \u0000${spokenSepToChar(sep)} `).trim().split(/\s+/);
    // Keep leading filler words ("es", "is") as plain text before the address.
    const lead = [];
    while (tokens.length > 1 && EMAIL_LEAD_STOP.has(tokens[0].toLowerCase()) && !tokens[1].startsWith('\u0000')) {
      lead.push(tokens.shift());
    }
    const user = tokens.map((t) => (t.startsWith('\u0000') ? t.slice(1) : t)).join('');
    const host = domain.replace(new RegExp(`\\s+${EMAIL_DOT}\\s+`, 'gi'), '.');
    const addr = `${user}@${host}`.toLowerCase();
    return lead.length ? `${lead.join(' ')} ${addr}` : addr;
  });
};

/** Merge non-overlapping typed units; earlier/longer wins on overlap. */
const mergeTypedUnits = (groups) => {
  const all = [];
  groups.forEach(({ type, units }) => {
    units.forEach((u) => all.push({ ...u, type }));
  });
  all.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  const out = [];
  all.forEach((u) => {
    if (out.some((x) => !(u.end <= x.start || u.start >= x.end))) return;
    out.push(u);
  });
  return out.sort((a, b) => a.start - b.start);
};

export const splitHighlightSegments = (text) => {
  if (!text) return [];
  const units = mergeTypedUnits([
    { type: 'date', units: findDateUnits(text) },
    { type: 'schedule', units: findScheduleUnits(text) },
    { type: 'dosage', units: findDosageUnits(text) },
    { type: 'vitals', units: findVitalsUnits(text) },
    { type: 'money', units: findMoneyUnits(text) },
    { type: 'address', units: findAddressUnits(text) },
    { type: 'email', units: findEmailUnits(text) },
    { type: 'email', units: findSpokenEmailUnits(text) },
  ]);
  const segments = [];
  let cursor = 0;

  const pushNumberSplits = (chunk) => {
    if (!chunk) return;
    const parts = chunk.split(NUMBER_HIGHLIGHT_REGEX);
    parts.forEach((p) => {
      if (!p) return;
      if (p.match(NUMBER_HIGHLIGHT_REGEX)) {
        segments.push({ type: 'number', value: p, copyValue: copyableDigits(p) });
      } else {
        segments.push({ type: 'text', value: p });
      }
    });
  };

  units.forEach((u) => {
    pushNumberSplits(text.slice(cursor, u.start));
    segments.push({ type: u.type, value: u.text, copyValue: u.copyValue });
    cursor = u.end;
  });
  pushNumberSplits(text.slice(cursor));
  return segments;
};

/** Clipboard value for a highlighted sensitive span. */
export const copyableSensitiveValue = (value, type = 'number') => {
  if (type === 'date') {
    const units = findDateUnits(String(value));
    if (units[0]?.copyValue) return units[0].copyValue;
    return String(value || '').trim();
  }
  if (type === 'schedule') {
    const units = findScheduleUnits(String(value));
    if (units[0]?.copyValue) return units[0].copyValue;
    return String(value || '').trim();
  }
  if (type === 'dosage') {
    const units = findDosageUnits(String(value));
    if (units[0]?.copyValue) return units[0].copyValue;
    return String(value || '').replace(/\s+/g, ' ').trim();
  }
  if (type === 'vitals') {
    const units = findVitalsUnits(String(value));
    if (units[0]?.copyValue) return units[0].copyValue;
    return String(value || '').replace(/\s+/g, ' ').trim();
  }
  if (type === 'money') {
    const units = findMoneyUnits(String(value));
    if (units[0]?.copyValue) return units[0].copyValue;
    return String(value || '').trim();
  }
  if (type === 'address') {
    const units = findAddressUnits(String(value));
    if (units[0]?.copyValue) return units[0].copyValue;
    return copyableDigits(value);
  }
  if (type === 'email') {
    const units = findEmailUnits(String(value));
    if (units[0]?.copyValue) return units[0].copyValue;
    const spoken = findSpokenEmailUnits(String(value));
    if (spoken[0]?.copyValue) return spoken[0].copyValue;
    return String(value || '').trim();
  }
  return copyableDigits(value);
};

export const stitchSingleDigitSequences = (text, { minDigits = 2, ignoreAddressGuard = false, ignoreDateGuard = false } = {}) => {
  if (!text) return text;
  return text.replace(SINGLE_DIGIT_RUN_RE, (match, offset, full) => {
    // v4.115.0: decimals are NEVER stitchable — "2.5 mg" must not become
    // "25 mg". A dot directly between digits means decimal/version, not dictation.
    if (/\d\.\d/.test(match)) return match;
    // v4.174.0: same rule for a COMMA. "1, 2, 3 tablets" is three doses and
    // "2, 5 mg" is a range-ish read; neither is a phone being dictated. A dot
    // was the only decimal guard, so comma-separated dose lists were silently
    // becoming one number ("123 tablets", "25 mg").
    if (/\d\s*,\s*\d/.test(match)) return match;
    // v4.122.0: two-part slash runs are fractions/BP ("1/2", "3/4") — never
    // stitch. Longer slash runs ("5/5/5/1/2/3/4") are dictation — stitch.
    if (match.includes('/') && match.split(/[\s,./-]+/).filter(Boolean).length <= 2) return match;
    const parts = match.split(/[\s,./-]+/).filter(Boolean);
    if (parts.length < minDigits || !parts.every((p) => /^\d$/.test(p))) return match;

    const before = full.slice(Math.max(0, offset - 40), offset);
    const after = full.slice(offset + match.length, offset + match.length + 40);
    if (!ignoreAddressGuard && looksLikeAddressFragment(before, after)) return match;
    if (!ignoreDateGuard && looksLikeDateFragment(before, after)) return match;
    // v4.174.0: a dose is NEVER dictation. "give 1 2 0 mg" is 120 mg; merging
    // it is right, but "1 2 3 tablets" is three separate tablets and must
    // stay readable as three. Only a unit in the window can tell them apart,
    // so a dosage cue next to the run keeps it spaced.
    if (DOSE_CUE_RE.test(`${before} ${after}`) && parts.length <= 3) return match;
    // v4.115.0: chart/MRN dictation stays spaced ("MRN 1 2 3 4 5 6 7 8" is
    // readable and safe). Narrow list on purpose: member/policy/afiliado IDs
    // belong to Phase G SSN grouping and must still stitch.
    if (/\b(?:mrn|chart|record|account|folio|expediente)\b/i.test(before)) return match;

    const trailingPunct = match.match(/[,.]$/)?.[0] || '';
    return parts.join('') + trailingPunct;
  });
};

/**
 * Sentinel display brakes (Phase C).
 * - Skip stitch: date/email/spelling (address may stitch — phone format still skipped)
 * - Skip phone/SSN format: those + dosage/medication/price (keep stitch for "5 0 0 mg")
 * - phone + ssn modes: full transforms allowed
 */
export const DISPLAY_SKIP_STITCH_MODES = new Set([
  'date',
  'email',
  'spelling',
]);

export const DISPLAY_SKIP_PHONE_FORMAT_MODES = new Set([
  'date',
  'address',
  'email',
  'spelling',
  'dosage',
  'medication',
  'price',
]);

export const shouldSkipDigitStitch = (mode) =>
  Boolean(mode && DISPLAY_SKIP_STITCH_MODES.has(mode));

export const shouldSkipPhoneFormat = (mode) =>
  Boolean(mode && DISPLAY_SKIP_PHONE_FORMAT_MODES.has(mode));

/** @deprecated use shouldSkipPhoneFormat — kept for older call sites */
export const shouldSkipPhoneDigitTransforms = (mode) => shouldSkipPhoneFormat(mode);

// ---------------------------------------------------------------------------
// VITALS (v4.120.0)
// ---------------------------------------------------------------------------
// "120 over 80" → "120/80" (BP, standard form — like clerk times).
// Weight/height/temp highlight + copy as one unit (display never rewrites
// these; the finder below only reports spans).
// ---------------------------------------------------------------------------

export const normalizeVitals = (text) => {
  if (!text) return text;
  return text.replace(/\b(\d{2,3})\s+(?:over|sobre)\s+(\d{2,3})\b/gi, '$1/$2');
};

const VITALS_UNITS_ALT =
  'lb|lbs|pounds?|kilos?|kg|libras?|ft|foot|feet|inch|inches|cm|pies|pulgadas?|cent[ií]metros?|degrees?|grados?|fahrenheit|celsius|centigrados?';

export const VITALS_RE = new RegExp(
  `\\b\\d+\\s*(?:ft|foot|feet)\\s*\\d+\\b|\\b\\d+(?:[.,]\\d+)?\\s*(?:${VITALS_UNITS_ALT}|°\\s*[FC])\\b|\\b\\d{2,3}(?:\\.\\d+)?\\s*(?:°\\s*)?[FC]\\b|\\b\\d{2,3}\\s*(?:/|\\s+(?:over|sobre)\\s+)\\s*\\d{2,3}\\b`,
  'i',
);

export const findVitalsUnits = (text) => {
  if (!text) return [];
  const units = [];
  const re = new RegExp(VITALS_RE.source, 'gi');
  let m;
  while ((m = re.exec(text))) {
    const raw = m[0];
    units.push({
      start: m.index,
      end: m.index + raw.length,
      text: raw,
      copyValue: raw.replace(/\s+/g, ' ').trim(),
    });
  }
  return units.sort((a, b) => a.start - b.start);
};
// ---------------------------------------------------------------------------
// CLERK TIME SHORTHAND (v4.114.0)
// ---------------------------------------------------------------------------
// Clerks list slots as "we have 1 1 30, 2 2 30" — that is NOT 1:30, it is
// TWO slots: 1:00 AND 1:30. Without this, stitch reads "1 1 3"→"113"+"0"
// ="1130" and phone-format then eats the whole list ("113-022-30").
// Expand FIRST (right after number-word conversion, before stitch/phone):
//   "1 1 30" → "1:00, 1:30"     "10 10 30" → "10:00, 10:30"
//   "H H 00" → "H:00" (single — avoids "3:00, 3:00" dupes)
// Hours are 1–12; minutes 00–59. H H H (phone-style repeats) never matches
// (third token must be 2 digits), so phone dictation is untouched.
// ---------------------------------------------------------------------------

export const CLERK_TIME_SHORTHAND_RE = /\b(\d{1,2})\s+\1\s+([0-5]\d)\b/;
const CLERK_TIME_SHORTHAND_G = new RegExp(CLERK_TIME_SHORTHAND_RE.source, 'g');

export const expandClerkTimeShorthand = (text) => {
  if (!text) return text;
  return text.replace(CLERK_TIME_SHORTHAND_G, (m, h, mm) => {
    const hour = parseInt(h, 10);
    if (hour < 1 || hour > 12) return m;
    if (mm === '00') return `${h}:00`;
    return `${h}:00, ${h}:${mm}`;
  });
};

/** Bare clock time after expansion — "1:30" with no am/pm. v4.115.0: 24h too. */
export const BARE_CLOCK_TIME_RE = /\b(?:[01]?\d|2[0-3]):[0-5]\d\b/;

/**
 * Find schedule/time spans for highlight/copy (v4.114.0).
 * v4.115.0: 24h "14:30" + bare "3pm" join H:MM.
 * Display-only — reports spans + copy value, never rewrites text.
 */
export const findScheduleUnits = (text) => {
  if (!text) return [];
  const units = [];
  const pushRe = (re) => {
    let m;
    while ((m = re.exec(text))) {
      const rawFull = m[1] || m[0];
      // v4.115.0: trailing space from "\s*(am|pm)?" is not part of the time.
      const raw = rawFull.replace(/\s+$/, '');
      const start = m.index + m[0].indexOf(rawFull);
      if (units.some((u) => !(start + raw.length <= u.start || start >= u.end))) continue;
      units.push({ start, end: start + raw.length, text: raw, copyValue: raw.trim() });
    }
  };
  pushRe(/\b((?:[01]?\d|2[0-3]):[0-5]\d\s*(?:a\.?m\.?|p\.?m\.?|am|pm)?)\b/gi);
  pushRe(/\b((?:[1-9]|1[0-2])\s*(?:a\.?m\.?|p\.?m\.?|am|pm))\b/gi);
  // v4.121.0: ranges — "1 to 3", "2-4", "entre las 2 y las 4".
  pushRe(/\b(([1-9]|1[0-2])(?::[0-5]\d)?\s*(?:to|–|-)\s*(?:[1-9]|1[0-2])(?::[0-5]\d)?)\b/gi);
  pushRe(/\b(entre las (?:[1-9]|1[0-2]) y las (?:[1-9]|1[0-2]))\b/gi);
  return units.sort((a, b) => a.start - b.start);
};

// ---------------------------------------------------------------------------
// WORD TIMES (v4.115.0)
// ---------------------------------------------------------------------------
// After number-word conversion "half past two" is "half past 2" and
// "tres y media" is "3 y media" — normalize to clock form so highlight,
// critical-data guards and translation safety see them:
//   "half past 2" → "2:30" · "3 y media" → "3:30" · "3 y cuarto" → "3:15"
//   "at 3 30" → "at 3:30" (cue-gated: at/around/about/a las/para las/de las)
// ---------------------------------------------------------------------------

export const expandWordTimes = (text) => {
  if (!text) return text;
  let out = text;
  out = out.replace(/\bhalf past (\d{1,2})\b/gi, '$1:30');
  // v4.121.0: quarter past/to, o'clock, en punto.
  out = out.replace(/\bquarter past (\d{1,2})\b/gi, (m, h) => {
    const n = parseInt(h, 10);
    return n >= 1 && n <= 12 ? `${n}:15` : m;
  });
  out = out.replace(/\bquarter to (\d{1,2})\b/gi, (m, h) => {
    const n = parseInt(h, 10);
    if (n < 1 || n > 12) return m;
    return `${n === 1 ? 12 : n - 1}:45`;
  });
  out = out.replace(/\b(\d{1,2})\s*o['’\-]?clock\b/gi, (m, h) => {
    const n = parseInt(h, 10);
    return n >= 1 && n <= 12 ? `${n}:00` : m;
  });
  out = out.replace(/\b(?:a\s+)?las\s+(\d{1,2})\s+en\s+punto\b/gi, (m, h) => {
    const n = parseInt(h, 10);
    return n >= 1 && n <= 12 ? `${n}:00` : m;
  });
  out = out.replace(/\b(\d{1,2})\s+y\s+media\b/gi, '$1:30');
  out = out.replace(/\b(\d{1,2})\s+y\s+cuarto\b/gi, '$1:15');
  out = out.replace(
    /((?:\bat|\baround|\babout|\ba las|\bpara las|\bde las)\s+)((?:[1-9]|1[0-2]))\s+([0-5]\d)\b/gi,
    '$1$2:$3',
  );
  return out;
};

// ---------------------------------------------------------------------------
// BARE TIMES (v4.121.0) — runs AFTER date masking, on masked text.
// ---------------------------------------------------------------------------
// "3 30" → "3:30" (H 1–12, MM 00–59). Masked dates are already out, and the
// negative lookahead skips triples ("05 12 1980", "12 34 56" stay).
//
// v4.173.0: this now REQUIRES a clock cue in the same window ("at", "am/pm",
// "o'clock", "appointment", "sharp", "hora"…) and a money cue VETOES it.
// Reason: spoken prices are exactly this shape — "one fifty", "two twenty
// five" — and turning "1 50" into "1:50" is a WRONG number on a cost call.
// A time is always cued in real speech; a price often is not, so the safe
// default is: no cue → leave the digits alone and let the reader see "1 50".
// ---------------------------------------------------------------------------

export const expandBareTimes = (text) => {
  if (!text) return text;
  return text.replace(/\b([1-9]|1[0-2])\s+([0-5]\d)\b(?!\s*\d)/g, (m, h, mm, off, full) => {
    if (moneyCueNear(full, off)) return m;
    return clockCueNear(full, off) ? `${h}:${mm}` : m;
  });
};

// ---------------------------------------------------------------------------
// ADJACENT DIGIT-REPEAT COLLAPSE (v4.117.0)
// ---------------------------------------------------------------------------
// Chunk straddles duplicate digit groups: "555 123" + "123 4567" merges to
// "555 123 123 4567" (overlap keeps both by design). Exact adjacent repeats
// of digit groups collapse to one copy — information-preserving, logged.
// NEVER collapses runs of identical single digits ("5 5 5 5" stays: that is
// dictation/stutter territory, and stitch owns it).
// ---------------------------------------------------------------------------

const isDigitGroupTok = (t) => {
  const s = (t || '').trim();
  return s.length > 0 && /[\d]/.test(s) && /^[\d\s.,/:-]+$/.test(s);
};

export const collapseAdjacentDigitRepeats = (text) => {
  if (!text) return text;
  const parts = text.match(/\S+\s*/g) || [];
  let collapsed = false;
  for (let k = 4; k >= 1; k -= 1) {
    for (let i = 0; i + 2 * k <= parts.length; i += 1) {
      const a = parts.slice(i, i + k);
      const b = parts.slice(i + k, i + 2 * k);
      if (!a.every(isDigitGroupTok) || !b.every(isDigitGroupTok)) continue;
      // v4.117.0: any run of single-digit tokens ("5 5 | 5 5", "9 1 | 9 1")
      // is dictation/stutter territory — keep both, stitch owns it. Only
      // multi-char groups ("123 | 123") collapse.
      if (a.every((t) => t.trim().length === 1)) continue;
      if (a.map((t) => t.trim()).join(' ') !== b.map((t) => t.trim()).join(' ')) continue;
      // v4.174.0: this is the ONLY stage that DELETES DIGITS, so it now
      // requires PROOF that the repeat is a chunk-straddle artefact and not
      // something the speaker said twice.
      //
      // A straddle dupe has an exact shape: ONE group repeated, sitting
      // between two OTHER digit groups. That is what "555 123" + "123 4567"
      // looks like after two chunks are joined. Anything wider (k > 1) or
      // without differing digit groups on both sides is just a number that
      // was said more than once — "we need 250 250 250 250 crowns" is a
      // price list, and collapsing it deleted two of the four prices.
      if (k !== 1) continue;
      const prev = (parts[i - 1] || '').trim();
      const next = (parts[i + 2] || '').trim();
      if (!isDigitGroupTok(prev) || !isDigitGroupTok(next)) continue;
      if (prev === a[0].trim() || next === a[0].trim()) continue;
      // Belt and braces: money or a dose in the window means two numbers.
      const win = parts
        .slice(Math.max(0, i - 6), i + 2 * k + 6)
        .join(' ');
      if (MONEY_CUE_RE.test(win) || DOSE_CUE_RE.test(win)) continue;
      const removed = parts.splice(i + k, k).join('').trim();

      collapsed = true;
      flagVanish('display_digit_repeat_collapse', {
        before: text,
        after: parts.join(''),
        stage: 'collapseAdjacentDigitRepeats',
        force: true,
        extra: { removed },
      });
      i -= 1;
    }
  }
  return collapsed ? parts.join('') : text;
};

// ---------------------------------------------------------------------------
// SPLIT-ZIP JOIN + DIRECTIONAL SLOT (v4.118.0)
// ---------------------------------------------------------------------------
// STT splits ZIPs ("California, 93, 550") and detaches compass letters
// ("3247 e Avenida", "3247 and Avenue" for 3247 E Avenue, "s 1" suite is a
// finder-level chip, not a rewrite). Both run pre-sentinel, both logged.
// ---------------------------------------------------------------------------

/** "California, 93, 550." → "California, 93550." — cue-gated, never blind. */
export const repairSplitZips = (text) => {
  if (!text) return text;
  const stateRe = new RegExp(`\\b(?:${US_STATE_NAMES})\\b`, 'i');
  const gateRe =
    /\b(?:zip(?:\s*code)?|postal(?:\s*code)?|c[oó]digo postal|calle|avenida|carrera|direcci[oó]n|domicilio|apartment|apt|suite|unit)\b/i;
  return text.replace(/\b(\d{2,3})\s*,\s*(\d{3})\b/g, (m, a, b, offset, full) => {
    const before = full.slice(Math.max(0, offset - 40), offset);
    // v4.174.0: two brakes. (1) MONEY — "the unit is 4 and the crowns are
    // 45, 250" satisfied the gate on the stray word "unit" and turned a price
    // list into 45250. A price is never a split ZIP. (2) the gate word must
    // be CLOSE to the pair, not anywhere in the previous 40 chars.
    if (MONEY_CUE_RE.test(before)) return m;
    const near = full.slice(Math.max(0, offset - 18), offset);
    if (!stateRe.test(near) && !gateRe.test(near)) return m;
    const joined = `${a}${b}`;
    flagVanish('display_zip_fragment_join', {
      before: m,
      after: joined,
      stage: 'repairSplitZips',
      force: true,
    });
    return joined;
  });
};

const COMPASS_LETTER = {
  e: 'E', east: 'E', este: 'E', y: 'E', and: 'E',
  w: 'W', west: 'W', oeste: 'W',
  n: 'N', north: 'N', norte: 'N',
  s: 'S', south: 'S', sur: 'S',
};

/**
 * Detached directional between house number and street type:
 * "3247 e Avenida" / "3247 and Avenue" → "3247 E Avenida" / "3247 E Avenue".
 * The street-type anchor keeps "fish and chips" and "bread and butter" away.
 */
export const normalizeAddressDirectionals = (text) => {
  if (!text) return text;
  const re = new RegExp(
    `\\b(\\d{1,6})\\s+(e|w|n|s|y|and|east|west|north|south|este|oeste|norte|sur)\\s+((?:[A-Za-zÁÉÍÓÚÑáéíóúñ][\\w.'-]*\\s+){0,2}${STREET_TYPE_WORD})\\b`,
    'gi',
  );
  return text.replace(re, (m, num, dir, rest) => {
    const letter = COMPASS_LETTER[dir.toLowerCase()] || dir;
    if (letter === dir) return m;
    flagVanish('display_directional_normalize', {
      before: m,
      after: `${num} ${letter} ${rest}`,
      stage: 'normalizeAddressDirectionals',
      force: true,
    });
    return `${num} ${letter} ${rest}`;
  });
};

// ---------------------------------------------------------------------------
// FRACTIONS + PERCENT (v4.122.0) — runs AFTER expandWordTimes so
// "half past two" is already "2:30" and never folds.
// ---------------------------------------------------------------------------
// "one half" → "1/2" · "three quarters" → "3/4" · "la mitad" → "1/2".
// Bare "half" folds ONLY for doses ("take half" → "take 1/2"); room
// "cuarto" folds ONLY as "un cuarto de" (else it is a bedroom).
// "50 percent" / "50 por ciento" → "50%".
// ---------------------------------------------------------------------------

export const foldFractions = (text) => {
  if (!text) return text;
  let out = text;
  out = out.replace(/\bone half\b|\ba half\b/gi, '1/2');
  out = out.replace(/\bthree quarters\b/gi, '3/4');
  out = out.replace(/\bone quarter\b/gi, '1/4');
  out = out.replace(/\ba quarter of\b/gi, '1/4 of');
  out = out.replace(/\bone third\b/gi, '1/3');
  out = out.replace(/\btwo thirds\b/gi, '2/3');
  out = out.replace(/\btake\s+half\b/gi, 'take 1/2');
  out = out.replace(/\bla mitad\b/gi, '1/2');
  out = out.replace(/\bmedia pastilla\b/gi, '1/2 pastilla');
  out = out.replace(/\bun tercio\b/gi, '1/3');
  out = out.replace(/\bdos tercios\b/gi, '2/3');
  out = out.replace(/\btres cuartos\b/gi, '3/4');
  out = out.replace(/\bun cuarto de\b/gi, '1/4 de');
  return out;
};

export const foldPercents = (text) => {
  if (!text) return text;
  let out = text;
  out = out.replace(/\b(\d+(?:[.,]\d+)?)\s*(?:percent|percentage)\b/gi, '$1%');
  out = out.replace(/\b(\d+(?:[.,]\d+)?)\s*por\s*ciento\b/gi, '$1%');
  return out;
};

/** Display pipeline order (transcript + translation panes). */
export const applyDisplayProtections = (text, lang = 'en', { applyNumberWords = true, expectedType = getArmedExpectedType(), allowDigitRewrites = isDigitRewritesEnabled() } = {}) => {
  if (!text) return text;
  // v4.173.0: the operator's panic button. OFF = show exactly what Deepgram
  // said. Every rewrite below is a guess about what a number meant, and on a
  // money call a wrong guess is worse than no formatting at all.
  if (!allowDigitRewrites) return text;
  let out = text;
  // v4.122.0: fractions/percent FIRST on words ("one half" → "1/2",
  // "50 por ciento" → "50%") — conversion would eat "one"/"ciento" first.
  out = foldFractions(out);
  out = foldPercents(out);
  // v4.120.0: "double five" → "five five", "8 oh 5" → "8 0 5" first.
  if (applyNumberWords) out = expandDictationWords(out, lang);
  if (applyNumberWords) out = convertEnglishNumberWords(out, lang);
  // v4.196.0: ES lane skips number words, but "ciento cincuenta pesos" → 150.
  if (!applyNumberWords && String(lang).slice(0, 2).toLowerCase() === 'es') out = foldSpanishSpokenMoney(out);
  // v4.173.0: fold spoken MONEY first — "one fifty" → 150 before
  // combineCompoundNumbers splits it and expandBareTimes turns it into 1:50.
  out = foldMoneyAmounts(out);
  // v4.115.0: "eighty two" → "82", "dos punto cinco" → "2.5" before stitch.
  if (applyNumberWords) out = combineCompoundNumbers(out, lang);
  // v4.120.0: "1 hundred 23" → "123", "2 mil 26" → "2026".
  if (applyNumberWords) out = foldMagnitudes(out);
  // v4.202.0: "juan punto r 56 arroba gmail punto com" → "juan.r56@gmail.com"
  // after number words became digits ("r fifty six" → "r 56").
  out = foldSpokenEmails(out);
  // v4.120.0: "120 over 80" → "120/80" before sentinels (standard vitals form).
  out = normalizeVitals(out);
  // v4.114.0: clerk "1 1 30" → "1:00, 1:30" BEFORE sentinels see the text,
  // so bare-time + schedule cues below can trigger the date display brake.
  out = expandClerkTimeShorthand(out);
  // v4.115.0: "half past 2" → "2:30", "at 3 30" → "at 3:30" before sentinels.
  // (fractions already folded above; "half past" contains no fold pattern.)
  out = expandWordTimes(out);
  // v4.117.0: straddle dupes ("555 123 123 4567") collapse before sentinels.
  out = collapseAdjacentDigitRepeats(out);
  // v4.118.0: address repairs — "93, 550" → "93550", "3247 e Avenida" →
  // "3247 E Avenida". Pre-sentinel so brakes see the joined form.
  out = repairSplitZips(out);
  out = normalizeAddressDirectionals(out);

  // Phase C: sentinels gate stitch/phone — display brake only (overlap unchanged).
  // ssn/phone sentinels win over address/date guards (Phase G tests): a dictated
  // ID/phone near "Madison Avenue" or "May 8" must still stitch + group.
  // v4.117.0: armed expectation (asked 2 bubbles ago) counts as a cue:
  // phone/ssn/member force full transform, dob/address force verbatim.
  const armedFull =
    expectedType === 'phone' || expectedType === 'ssn' || expectedType === 'member';
  const armedSkip =
    !armedFull && (expectedType === 'dob' || expectedType === 'address');
  const sentinel = detectSentinelContext(out, lang);
  const skipStitch =
    !armedFull && (shouldSkipDigitStitch(sentinel.mode) || armedSkip);
  const skipPhone =
    !armedFull && (shouldSkipPhoneFormat(sentinel.mode) || armedSkip);
  const fullTransform =
    sentinel.mode === 'ssn' || sentinel.mode === 'phone' || armedFull;
  const stitchOpts = {
    ignoreAddressGuard: sentinel.mode === 'address' || fullTransform,
    ignoreDateGuard: fullTransform,
    // v4.116.0: explicit phone/ssn cue ("my phone is ...") still groups odd
    // lengths; everything else leaves non-US lengths verbatim.
    allowNonStandardDash: fullTransform,
    // v4.117.0: armed request ("asked 2 bubbles ago") overrides ZIP/MRN brakes.
    expectedType: expectedType || null,
    // v4.197.0: SSN dash shape needs an SSN/ID cue somewhere in this text.
    ssnCue: sentinel.mode === 'ssn',
  };

  const { text: masked, restore } = maskDateUnits(out);
  // v4.121.0: cueless "3 30" → "3:30" on masked text (dates already safe).
  out = expandBareTimes(masked);

  if (!skipStitch) {
    const afterWords = out;
    out = stitchSingleDigitSequences(out, stitchOpts);
    if (out !== afterWords) {
      flagVanish('display_digit_stitch', {
        before: afterWords,
        after: out,
        stage: 'applyDisplayProtections',
        force: true,
        extra: { step: 'stitchSingleDigitSequences', sentinel: sentinel.mode },
      });
    }
  }

  if (!skipPhone) {
    const afterStitch = out;
    out = formatPhoneAndSSNDigits(out, stitchOpts);
    if (out !== afterStitch && /\d/.test(afterStitch)) {
      flagVanish('display_phone_ssn_reformat', {
        before: afterStitch,
        after: out,
        stage: 'applyDisplayProtections',
        force: true,
        extra: { step: 'formatPhoneAndSSNDigits', sentinel: sentinel.mode },
      });
    }
  }

  out = repairNYCZipNumbers(out);
  out = restore(out);
  return out;
};

export const copyableDigits = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits || String(value || '').trim();
};

// NYC ZIP REPAIR — "New York, 10023" → "New York 10023" (comma only).
// v4.174.0: this used to PREFIX an invented "100" to any 3-digit number after
// the city, so "New York, 250" became "New York 10050" — digits the speaker
// never said, on a call where the number was a price. It now only tidies a
// 5-digit ZIP that already carries its own leading 1, which is the only shape
// this rule was ever right about. Anything else stays exactly as dictated.
export const repairNYCZipNumbers = (text) => {
  if (!text) return text;
  return text.replace(
    /\b(New York|NY|N\.Y\.)\s*,\s*(1\d{4})\b/gi,
    (m, city, zip) => `${city} ${zip}`,
  );
};

// NÚMEROS MÁGICOS — unchanged from v4.27
export const NUMBER_HIGHLIGHT_REGEX = /(\+?\(?\d{1,4}?\)?[\s.-]?\(?\d{2,4}?\)?[\s.-]?\d{3,4}[\s.-]?\d{3,4}|\b\d+[\d.,/\\-]*\b)/g;
export const getNumberHighlightRegex = () =>
  new RegExp(NUMBER_HIGHLIGHT_REGEX.source, 'g');

// ---------------------------------------------------------------------------
// CRITICAL MEDICAL/ADMIN DATA PROTECTION (pure regex + helpers)
// ---------------------------------------------------------------------------

const EN_DATE_CUE_RE =
  /\b(date|when|scheduled|schedule|appointment|appt|follow[- ]?up|rescheduled|booked|due|available|availability|spots?|openings?|slots?|birth|born|dob|age|aged)\b|how old/i;

const ES_DATE_CUE_RE =
  /\b(fecha|cu[aá]ndo|programad[oa]|agendad[oa]|turno|cita|control|seguimiento|reprogramad[oa]|vence|disponibles?|disponibilidad|huecos?|cupos?|turnos?)\b/i;

const MONTH_RE =
  /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?|enero|febrero|marzo|abril|junio|julio|agosto|septiembre|setiembre|octubre|noviembre|diciembre)\b/i;

const MAY_DATE_RE =
  /\bmay\s+(?:\d{1,2}|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|thirteenth|fourteenth|fifteenth|sixteenth|seventeenth|eighteenth|nineteenth|twentieth|twenty[-\s]first|twenty[-\s]second|twenty[-\s]third|thirtieth|thirty[-\s]first)\b/i;

const WEEKDAY_RE =
  /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday|lunes|martes|mi[eé]rcoles|jueves|viernes|s[aá]bado|domingo)\b/i;

const ORDINAL_RE =
  /\b(first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|thirteenth|fourteenth|fifteenth|sixteenth|seventeenth|eighteenth|nineteenth|twentieth|twenty[-\s]first|twenty[-\s]second|twenty[-\s]third|thirtieth|thirty[-\s]first|primero|segunda?|tercero|cuarto|quinto|sexto|s[eé]ptimo|octavo|noveno|d[eé]cimo)\b/i;

const NUMERIC_DATE_RE = /\b\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?\b|\b\d{1,2}\.\d{1,2}\.\d{4}\b/;

// v4.115.0: slow spaced dictation "my DOB is 05 12 1980" — cue required so
// random triples ("take 2 12 pills") never match.
const SPACED_DOB_RE =
  /\b(?:born|dob|birthday|appointment|appt|fecha|nacimiento|cita)\b[^.\n]{0,40}?\d{1,2}\s+\d{1,2}\s+\d{4}\b/i;

const CLOCK_TIME_RE =
  /\b\d{1,2}(?::\d{2})?\s*(?:a\.?m\.?|p\.?m\.?|am|pm)\b/i;

const MED_CUE_RE =
  /\b(medication|medicine|med|dose|dosage|prescription|prescribed|pharmacy|pill|tablet|capsule|insulin|medicamento|medicina|dosis|receta|recetad[oa]|farmacia|pastilla|comprimido|c[aá]psula|insulina|gotas?|puffs?|blood pressure|weight|height|fever|temperatura|fiebre|presi[oó]n|peso|talla|estatura)\b/i;

const DOSAGE_RE = new RegExp(`\\b\\d+(?:[.,]\\d+)?\\s*(?:${DOSAGE_UNITS_ALT})\\b`, 'i');

// Spelled-out doses: "five hundred milligrams", "twenty units" — nurse dictation
// often has no digits, so DOSAGE_RE alone never fires and the stutter-prune eats it.
const WORD_DOSAGE_RE = new RegExp(
  `\\b(?:zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|thousand|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce|trece|catorce|quince|veinte|treinta|cuarenta|cincuenta|sesenta|setenta|ochenta|noventa|cien|mil)(?:[-\\s](?:one|two|three|four|five|six|seven|eight|nine|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|hundred|thousand|cien|mil))?\\s+(?:${DOSAGE_UNITS_ALT})\\b`,
  'i',
);

const FREQUENCY_RE =
  /\b(once|twice|daily|nightly|every|per day|a day|bid|tid|qid|prn|una vez|dos veces|diari[oa]|cada|por d[ií]a|por noche)\b/i;

const PRICE_CUE_RE =
  /\b(price|cost|costs|charge|fee|pay|paid|payment|copay|co-pay|consultation|procedure|medication|medicine|cuesta|costo|precio|pagar|pag[oó]|cobran|consulta|procedimiento|medicamento|medicina|copago|coaseguro|self.?pay|uninsured|underinsured|deductible|deducible|sin seguro|pago privado)\b|out[- ]of[- ]pocket/i;

const MONEY_RE =
  /(?:[$€£]\s*\d+(?:[.,]\d{3})*(?:[.,]\d{2})?|\b\d+(?:(?:[.,]\d{3})+)?(?:[.,]\d{2})?\s*(?:dollars?|pesos?|usd|ars|copay|co-pay|copago|coaseguro)\b)/i;

// v4.122.0: percentages read as data, not filler.
const PERCENT_RE = /\b\d+(?:[.,]\d+)?\s*(?:%|percent|percentage|por ciento)\b/i;

export const hasCriticalDataCue = (text) => {
  if (!text) return false;
  return (
    EN_DATE_CUE_RE.test(text) ||
    ES_DATE_CUE_RE.test(text) ||
    MED_CUE_RE.test(text) ||
    PRICE_CUE_RE.test(text)
  );
};

export const containsCriticalData = (text) => {
  if (!text) return false;

  return (
    NUMERIC_DATE_RE.test(text) ||
    SPACED_DOB_RE.test(text) ||
    CLOCK_TIME_RE.test(text) ||
    BARE_CLOCK_TIME_RE.test(text) ||
    CLERK_TIME_SHORTHAND_RE.test(text) ||
    MONTH_RE.test(text) ||
    MAY_DATE_RE.test(text) ||
    WEEKDAY_RE.test(text) ||
    ORDINAL_RE.test(text) ||
    DOSAGE_RE.test(text) ||
    WORD_DOSAGE_RE.test(text) ||
    VITALS_RE.test(text) ||
    PERCENT_RE.test(text) ||
    MONEY_RE.test(text) ||
    (MED_CUE_RE.test(text) && FREQUENCY_RE.test(text)) ||
    (PRICE_CUE_RE.test(text) && /\d/.test(text))
  );
};

// ---------------------------------------------------------------------------
// CONTEXT SENTINELS
// ---------------------------------------------------------------------------
// Detects "about to spell something" contexts so downstream STT logic can
// switch into pass-through / protected mode and avoid mangling proper nouns,
// emails, and addresses.
//
// Returns an object:
//   { mode: 'spelling' | 'email' | 'ssn' | 'phone' | 'address' | 'date' | 'medication' | 'dosage' | 'price' | null,
//     lang: 'en' | 'es' }
//
// Call this BEFORE convertEnglishNumberWords or formatPhoneAndSSNDigits.
// If mode is non-null, skip or limit further transformations.
// ---------------------------------------------------------------------------

const EN_SENTINELS = {
  spelling: [
    /\bspell(ing)?\b/i,
    /\bspelled\b/i,
    /\bmy name is\b/i,
    /\blast name\b/i,
    /\bfirst name\b/i,
    /\bsurname\b/i,
  ],
  email: [
    /\be-?mail\b/i,
    /\b@\b/,
    /\bdot com\b/i,
    /\bat the rate\b/i,
    /\bat sign\b/i,
  ],
  ssn: [
    /\bsocial\b/i,
    /\bssn\b/i,
    /\bsocial security\b/i,
    /\bsecurity number\b/i,
    // v4.117.0: request phrasings — "can I have your social?"
    /can i have your (ssn|social|security)/i,
    /what is your (ssn|social)/i,
    // Phase G: insurance IDs (member/affiliate/Medicaid) — same digit unit as SSN.
    // v4.121.0: case/claim/reference IDs join the lane (ID noun required —
    // bare "in case you" must not arm).
    /\bmedicaid\b/i,
    /\baffiliate[sd]?\b/i,
    /\bmember (id|number)\b/i,
    /\b(id|identification) number\b/i,
    /\bpolicy number\b/i,
    /\binsurance id\b/i,
    /\b(?:case|claim|group|reference|confirmation|authorization|auth)\s*(?:id|number|#)/i,
  ],
  phone: [
    /\bphone\b/i,
    /\bcell\b/i,
    /\bmobile\b/i,
    // v4.117.0: request phrasings — "can I have your phone number?"
    /can i have your (phone|cell|mobile|contact|number)/i,
    /what is your (phone|cell|mobile|contact)/i,
    // v4.121.0: narrowed — bare "number is" fired on room/case/MRN numbers
    // and dashed them as phones. Needs a phone-ish noun or a digit ahead.
    /(?:phone|cell|mobile|contact|member)\s+number\s+is\b/i,
    /\bnumber\s+is\s+\d/i,
    /\bcall me at\b/i,
    /\bcontact number\b/i,
    /\bcall back\b/i,
  ],
  address: [
    /\b(mailing|home|street|billing)?\s*address\b/i,
    /\b(?:for\s+)?verification\b/i,
    /\bhipaa\b/i,
    /\blive(s)? (at|on)\b/i,
    /\bzip( code)?\b/i,
    /\bapartment\b/i,
    /\bfloor\b/i,
    /\bsuite\b/i,
    /\b(north|south|east|west)\b/i,
    /\bavenue\b/i,
    /\bstreet\b/i,
    /\bboulevard\b/i,
    /\bdrive\b/i,
    /\broad\b/i,
  ],
  date: [
    EN_DATE_CUE_RE,
    MONTH_RE,
    WEEKDAY_RE,
    NUMERIC_DATE_RE,
    SPACED_DOB_RE,
    CLOCK_TIME_RE,
    // v4.114.0: clerk slot lists + expanded bare times trigger the date
    // display brake (skip stitch/phone) even with no other cue words.
    CLERK_TIME_SHORTHAND_RE,
    BARE_CLOCK_TIME_RE,
  ],
  medication: [MED_CUE_RE],
  dosage: [MED_CUE_RE, DOSAGE_RE, FREQUENCY_RE],
  price: [PRICE_CUE_RE, MONEY_RE],
};

const ES_SENTINELS = {
  spelling: [
    /\b(c[oó]mo se escribe|deletrea[r]?|se escribe)\b/i,
    /\bnombre\b/i,
    /\bapellido\b/i,
  ],
  email: [
    /\bcorreo\b/i,
    /\be-?mail\b/i,
    /\barroba\b/i,
    /\bpunto com\b/i,
  ],
  ssn: [
    /\bseguro social\b/i,
    /\bnúmero de seguro\b/i,
    /\bssn\b/i,
    // v4.117.0: request phrasings — "me puede dar su seguro?"
    /(me puede dar|d[ií]game|cu[aá]l es).{0,25}(seguro|afiliado|miembro|medicaid)/i,
    // Phase G: insurance IDs (afiliado/Medicaid) — same digit unit as SSN.
    /\bmedicaid\b/i,
    /\bafiliad[oa]s?\b/i,
    /\bnúmero de identificaci[oó]n\b/i,
    /\bidentificaci[oó]n\b/i,
    /\bnúmero de miembro\b/i,
    /\bp[oó]liza\b/i,
  ],
  phone: [
    /\btelefono\b/i,
    /\bcel(ular)?\b/i,
    /\bnúmero de tel[eé]fono\b/i,
    /\bllamar al\b/i,
    // v4.117.0: request phrasings — "me puede dar su número?"
    /dame (tu|su) n[uú]mero\b/i,
    /(me puede dar|d[ií]game|cu[aá]l es).{0,25}(tel[eé]fono|celular|n[uú]mero)/i,
  ],
  address: [
    /\bdirecci[oó]n\b/i,
    /\bcalle\b/i,
    /\bavenida\b/i,
    /\bcodigo postal\b/i,
    /\bapartamento\b/i,
    /\bpiso\b/i,
  ],
  date: [
    ES_DATE_CUE_RE,
    MONTH_RE,
    WEEKDAY_RE,
    NUMERIC_DATE_RE,
    SPACED_DOB_RE,
    CLOCK_TIME_RE,
    // v4.114.0: same clerk/bare-time brake for the ES lane.
    CLERK_TIME_SHORTHAND_RE,
    BARE_CLOCK_TIME_RE,
  ],
  medication: [MED_CUE_RE],
  dosage: [MED_CUE_RE, DOSAGE_RE, FREQUENCY_RE],
  price: [PRICE_CUE_RE, MONEY_RE],
};

export const detectSentinelContext = (text, lang = 'en') => {
  if (!text) return { mode: null, lang };
  const sentinels = lang === 'es' ? ES_SENTINELS : EN_SENTINELS;
  for (const [mode, patterns] of Object.entries(sentinels)) {
    if (patterns.some((re) => re.test(text))) {
      return { mode, lang };
    }
  }
  return { mode: null, lang };
};

// ---------------------------------------------------------------------------
// ADDRESS-AWARE digit protection
// ---------------------------------------------------------------------------
// US addresses can contain numeric street names: "20 West 34th Street".
// Naively joining/reformatting those digits breaks the address.
// This function checks if a digit sequence is likely part of an address
// (preceded or followed by directional/street-type words) and if so,
// returns the digits as-is rather than phone-formatting them.
//
// Used internally by formatPhoneAndSSNDigits and exported for testing.
// ---------------------------------------------------------------------------

const STREET_TYPE_RE = /\b(street|st|avenue|ave|boulevard|blvd|drive|dr|road|rd|lane|ln|court|ct|place|pl|way|circle|cir|highway|hwy|route|rt|parkway|pkwy|terrace|ter|calle|avenida|av|carrera|cra|bulevar)\b/i;
const DIRECTION_RE = /\b(north|south|east|west|northeast|northwest|southeast|southwest|n|s|e|w|ne|nw|se|sw)\b/i;

export const looksLikeAddressFragment = (textBefore, textAfter) => {
  const before = (textBefore || '').slice(-40);
  const after = (textAfter || '').slice(0, 40);
  return (
    STREET_TYPE_RE.test(before) ||
    STREET_TYPE_RE.test(after) ||
    DIRECTION_RE.test(before) ||
    DIRECTION_RE.test(after)
  );
};

// ---------------------------------------------------------------------------
// STT / protection helpers (unchanged signatures)
// ---------------------------------------------------------------------------

const normalize = (s) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

export const NUMBER_WORDS = new Set([
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight',
  'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen',
  'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty', 'thirty', 'forty',
  'fifty', 'sixty', 'seventy', 'eighty', 'ninety', 'hundred', 'thousand',
  'cero', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho',
  'nueve', 'diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciseis',
  'diecisiete', 'dieciocho', 'diecinueve', 'veinte', 'veintiuno', 'veintidos',
  'veintitres', 'veinticuatro', 'veinticinco', 'veintiseis', 'veintisiete',
  'veintiocho', 'veintinueve', 'treinta', 'cuarenta', 'cincuenta', 'sesenta',
  'setenta', 'ochenta', 'noventa', 'cien', 'ciento', 'mil',
]);

export const normalizeWord = (w) =>
  w
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const isNumberLike = (word) => {
  if (!word) return false;
  const norm = normalizeWord(word);
  return /^\d+$/.test(norm) || NUMBER_WORDS.has(norm);
};

export const containsNumberSequence = (text, minLength = 2) => {
  const words = (text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return false;

  let count = 0;
  let numberWords = 0;
  for (const w of words) {
    if (isNumberLike(w)) {
      count++;
      numberWords++;
      if (count >= minLength) return true;
    } else {
      count = 0;
    }
  }

  if (numberWords / words.length > 0.3) return true;
  return false;
};

const FILLER_WORDS = new Set([
  'um', 'uh', 'eh', 'ah', 'like', 'well', 'so', 'okay', 'ok', 'yeah',
  'yep', 'nope', 'hmm', 'hm', 'bueno', 'pues', 'este', 'ees', 'ehm',
]);

// Dedupe must never eat these — clinical repeats ("take take" → drop is fine,
// but "insulin insulin" during a correction can be the nurse re-stressing).
// v4.133.0: also med NAMES + trauma words (not just cues) — a trauma doctor
// mid-tirade repeating a drug name is emphasis, never stutter.
const DEDUPE_NEVER_DROP = new Set([
  'mg', 'ml', 'units', 'unidades', 'insulin', 'insulina', 'metformin',
  'metformina', 'milligrams', 'miligramos', 'twice', 'daily', 'diario',
  'not', 'no', 'never', 'nunca', 'allergic', 'alergica',
  'alergico', 'without', 'sin',
  'warfarin', 'lisinopril', 'aspirin', 'ibuprofen', 'acetaminophen', 'tylenol',
  'morphine', 'fentanyl', 'epinephrine', 'adrenaline', 'heparin', 'penicillin',
  'amoxicillin', 'azithromycin', 'cephalexin', 'metoprolol', 'atorvastatin',
  'furosemide', 'hydrocortisone', 'prednisone', 'albuterol', 'salbutamol',
  'diazepam', 'lorazepam', 'haloperidol', 'ketamine', 'propofol', 'ondansetron',
  'omeprazole', 'losartan', 'amlodipine', 'clonidine', 'hydralazine', 'labetalol',
  'nitroglycerin', 'dopamine', 'dobutamine', 'norepinephrine', 'vasopressin',
  'tranexamic', 'txa', 'naloxone', 'flumazenil', 'lidocaine', 'bupivacaine',
  'ceftriaxone', 'vancomycin', 'gentamicin', 'ciprofloxacin', 'levetiracetam',
  'phenytoin', 'dextrose', 'potassium', 'magnesium', 'calcium', 'sodium',
  'normal', 'saline', 'lactated', 'ringers', 'cpr', 'compressions', 'defibrillator',
  'intubate', 'ventilator', 'tourniquet', 'bleeding', 'airway', 'seizure',
  'stroke', 'trauma', 'code', 'stat',
]);

// v4.174.0: phrase fillers are NOT stripped any more.
// They used to be removed from the MIDDLE of every finalized sentence, which
// could destroy meaning: "Do you know if you have insurance?" became "Do if
// you have insurance?" — a question turned into a fragment, in the text the
// translator then translated. A readability win is never worth changing what
// the patient said. Leading single-word fillers below still go.
const PHRASE_FILLERS = [];

export const cleanFillerWords = (text) => {
  if (!text) return text;
  let t = text;

  PHRASE_FILLERS.forEach((phrase) => {
    const re = new RegExp(`\\b${phrase}\\b`, 'gi');
    t = t.replace(re, '');
  });

  const words = t.trim().split(/\s+/);
  let startIdx = 0;
  while (
    startIdx < words.length &&
    FILLER_WORDS.has(words[startIdx].toLowerCase().replace(/[^a-z]/g, ''))
  ) {
    startIdx++;
  }

  if (startIdx > 0) {
    t = words.slice(startIdx).join(' ');
  }

  return t.replace(/\s+/g, ' ').trim();
};

/**
 * v4.196.0 — "es Juan, Juan Perez" / "my name is David, David Smith".
 * Deepgram capitalizes names. Two Capitalized copies of a word, where the
 * second is NOT a sentence start, is a person restating a name, not a
 * stutter: keep both. ("The. The" after a period still dedupes.)
 */
export const isRepeatedProperName = (prevRaw, curRaw) => {
  const prev = String(prevRaw || '');
  const cur = String(curRaw || '');
  const startsUpper = (w) => /^\p{Lu}/u.test(w.replace(/^[^\p{L}]+/u, ''));
  if (!startsUpper(prev) || !startsUpper(cur)) return false;
  return !/[.!?]["')\]]*$/.test(prev);
};

export const hallucinationGuard = (text) => {
  if (!text) return text;
  const words = text.trim().split(/\s+/);

  if (words.length === 1) {
    const w = words[0].toLowerCase();
    if (isNumberLike(w)) return text;
    if (w === 'bueno' || w === 'um' || w === 'eh' || w === 'uh' || w === 'ah') {
      flagVanish('hallucination_filler_wipe', {
        before: text,
        after: '',
        stage: 'hallucinationGuard',
        derender: true,
        force: true,
        extra: { filler: w },
      });
      return '';
    }
  }

  if (words.length < 2) return text;

  let cleaned = [];
  let lastWord = '';
  let lastPair = '';

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const norm = normalize(word);
    const pair = i > 0 ? normalize(words[i - 1] + word) : '';

    const localWindow = words
      .slice(Math.max(0, i - 4), i + 5)
      .join(' ');
    const pairWindow = words
      .slice(Math.max(0, i - 5), i + 6)
      .join(' ');

    if (
      norm === lastWord &&
      norm.length > 1 &&
      !isNumberLike(word) &&
      !DEDUPE_NEVER_DROP.has(norm) &&
      !isRepeatedProperName(words[i - 1], word) &&
      !containsCriticalData(localWindow) &&
      !hasCriticalDataCue(localWindow)
    )
      continue;
    if (
      pair === lastPair &&
      pair.length > 4 &&
      !DEDUPE_NEVER_DROP.has(norm) &&
      !containsNumberSequence(words.slice(i - 1, i + 1).join(' ')) &&
      !containsCriticalData(pairWindow) &&
      !hasCriticalDataCue(pairWindow)
    )
      continue;

    cleaned.push(word);
    lastWord = norm;
    lastPair = pair;
  }

  // v4.187.0: heavy stutter is LOGGED only. It used to cut the whole bubble to
  // 12 words + "[Stutter Pruned]" — deleting real words already on screen.
  // The dedupe above already removed the repeats; keep every other word.
  if (words.length > 15 && cleaned.length < words.length * 0.5) {
    flagVanish('hallucination_stutter_heavy', {
      before: text,
      after: cleaned.join(' '),
      stage: 'hallucinationGuard',
      force: true,
    });
  }

  const out = cleanFillerWords(cleaned.join(' '));
  if (out !== text && cleaned.length < words.length) {
    flagVanish('hallucination_stutter_dedupe', {
      before: text,
      after: out,
      stage: 'hallucinationGuard',
    });
  }
  return out;
};

export const removeOverlapPreservingDigitSequences = (base, addition) => {
  if (!base || !addition) return addition;

  const bWords = base.trim().split(/\s+/).map(normalize);
  const aWords = addition.trim().split(/\s+/).map(normalize);
  const aWordsRaw = addition.trim().split(/\s+/);

  // v4.116.0 "never destroy rendered numbers": the normalized join('')
  // treats "12 34" ≡ "1234" ≡ "1 2 3 4". When digits sit near the boundary,
  // only an EXACT raw-word repeat counts as overlap — when in doubt both
  // copies stay on screen and downstream guards sort it out.
  // v4.133.0 (ER incident): a tirade repeats medical words across the
  // boundary ("airway airway", "epinephrine epinephrine") — those are
  // emphasis, never overlap. Same rule: exact repeat only.
  // NOTE: a genuine 1-word echo ("the airway" → "the airway is patent")
  // still strips — the clinical list only protects *emphasis* repeats like
  // epinephrine/airway; plain function words still dedupe across chunks.
  const boundaryWindow = `${base.trim().split(/\s+/).slice(-10).join(' ')} ${aWordsRaw.slice(0, 10).join(' ')}`;
  const boundaryHasDigits = /\d/.test(boundaryWindow);
  const boundaryHasClinical = boundaryWindow
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .some((w) => DEDUPE_NEVER_DROP.has(w));
  const useExactRepeat = boundaryHasDigits || boundaryHasClinical;
  const bCmp = useExactRepeat
    ? base.trim().split(/\s+/).map((w) => w.toLowerCase())
    : bWords;
  const aCmp = useExactRepeat
    ? aWordsRaw.map((w) => w.toLowerCase())
    : aWords;
  const joiner = useExactRepeat ? ' ' : '';

  let bestOverlap = 0;
  const maxCheck = Math.min(aWords.length, bWords.length, 50);

  for (let i = 1; i <= maxCheck; i++) {
    const aPrefix = aCmp.slice(0, i).join(joiner);
    const bSuffix = bCmp.slice(-i).join(joiner);
    if (aPrefix === bSuffix) {
      bestOverlap = i;
    }
  }

  if (bestOverlap > 0) {
    const overlapSlice = aWordsRaw.slice(0, bestOverlap);
    const overlapText = overlapSlice.join(' ');
    const boundaryText = [
      base.trim().split(/\s+/).slice(-10).join(' '),
      aWordsRaw
        .slice(0, Math.min(aWordsRaw.length, bestOverlap + 8))
        .join(' '),
    ].join(' ');
    // v4.199.0: was a console.log per packet in dev — now silent.
    if (
      /\d/.test(overlapText) ||
      overlapSlice.some(isNumberLike) ||
      containsCriticalData(overlapText) ||
      containsCriticalData(boundaryText) ||
      hasCriticalDataCue(boundaryText) ||
      overlapSlice.some((w) => DEDUPE_NEVER_DROP.has(normalize(w))) ||
      // v4.196.0: "es Juan," + "Juan Perez" — a 1-word name echo that opens a
      // full name (next word also Capitalized) is a restated name, not overlap.
      (bestOverlap === 1 &&
        isRepeatedProperName(base.trim().split(/\s+/).slice(-1)[0], aWordsRaw[0]) &&
        isRepeatedProperName(aWordsRaw[0], aWordsRaw[1]))
    ) {
      bestOverlap = 0;
    }
  }

  const result = aWordsRaw.slice(bestOverlap).join(' ');
  if (bestOverlap > 0 && result !== addition) {
    flagVanish('overlap_strip', {
      before: addition,
      after: result,
      stage: 'removeOverlapPreservingDigitSequences',
      force: true,
      extra: { bestOverlap, baseTail: base.trim().split(/\s+/).slice(-8).join(' ') },
    });
  }
  return result;
};
